/*
 * HUM‑ID Sensor‑Planer – v47 public safe
 * – basiert auf v39 public safe.
 * – Neu: Upload Spezial für hochauflösendes PDF/PNG-Rendering.
 * – Neu: DXF-Export der Sensorpunkte/Auras/Polygonfläche für CAD-Workflows.
 * v41:
 * – Neuer Schritt 6 „Druck & Export“: PDF-Rahmen (Projektangaben), manuelle
 *   Produktübersicht, weiße Abdeckblöcke auf der Stage, PDF-Vorschau.
 * – Neues PDF-Layout (Basis- und Rahmen-Variante) mit HUM-ID Branding,
 *   Kontakt-Fußzeile und projektbezogenem Dateinamen.
 * – Angebots-Modal öffnet das Mailprogramm (Preisanfrage an info@hum-id.com).
 * – Fortschrittsanzeige (Schritte 1–6), Fokus-Zustände, Btn auf Modulebene.
 * – .humidplan Version 2 (print-Block), v1-Dateien laden unverändert.
 * v45:
 * – Hintergrundfarbe/-transparenz für die Sensorfläche, auch im PDF-Printout.
 * – Aura-Farbe per Kontextmenü, mit weißer Kontur am Aura-Mittelpunkt.
 * – Rahmen-PDF zeigt in der Fußzeile nur noch selbst gesetzte Kennzahlen.
 * v46:
 * – AURA-Reichweitenkreise erhalten eine manuell einstellbare Darstellungsgröße.
 * – Optionaler Modus „Aura-Nummern“ blendet Reichweitenkreise aus und nummeriert
 *   alle AURA-Messpunkte automatisch und lückenlos in der Reihenfolge 1, 2, 3 …
 * v47:
 * – Im Nummernmodus bleiben AURA-Messpunkte als größere nummerierte Marker mit
 *   kleinem transparentem AURA-Kreis sichtbar. Derselbe Größenregler skaliert
 *   dort Zahl, Mittelpunkt und transparenten Kreis gemeinsam.
 * – Rechtsklick auf AURA-Mittelpunkte ist in allen Bearbeitungsmodi zuverlässig
 *   aktiv; die Farbe lässt sich wie bei passiven Sensoren über „Einfärben“ ändern.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { jsPDF } from "jspdf";
import {
  Stage,
  Layer,
  Line,
  Image as KonvaImage,
  Circle,
  Group,
  Rect,
  Shape,
  Text,
} from "react-konva";
import "./style.css";
import * as pdfjsLib from "pdfjs-dist/build/pdf";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const MAX_IMG_WIDTH = 3000;
const JPG_QUALITY = 0.92;
const TARGET_IMG_MAX_H = 900;

// Spezial-Upload: PDF deutlich höher auflösen und als PNG behalten.
// Das PDF wird weiterhin browserbasiert gerendert, aber ohne JPEG-Kompression.
const SPECIAL_PDF_TARGET_SCALE = 4;
const SPECIAL_PDF_MAX_WIDTH = 7000;
const SPECIAL_PDF_MAX_HEIGHT = 7000;
const SPECIAL_PDF_MAX_PIXELS = 36000000;

// CAD-Export: passive Sensoren als kleine CAD-Kreise, Auras mit 10 m Radius.
const DXF_SENSOR_RADIUS_M = 0.12;
const DXF_TEXT_HEIGHT_M = 1.2;

/* ──────────────────────────────────────────────────────────────
 * PDF-ASSETS (Logo + Schrift)
 * Die Assets werden erst beim PDF-Export geladen. Dadurch bleibt
 * die App auch dann nutzbar, wenn ein externer Asset-Request scheitert.
 * ────────────────────────────────────────────────────────────── */
const PDF_LOGO_URLS = [
  "https://www.hum-id.com/vertrieb/logowhite.png",
  "https://www.hum-id.com/new/logo-website-big-01.png",
];

/* v44: farbiges HUM-ID Logo für die Fußzeile des Rahmen-Layouts – ohne
   dunklen Kasten, direkt auf weißem Papier. */
const PDF_FOOTER_LOGO_URLS = [
  "https://www.hum-id.com/tool/assets/logo-unten-rechts.jpg",
];

const FIRA_SANS_REGULAR_URLS = [
  "https://www.hum-id.com/new/FiraSans-Regular.ttf",
  "https://raw.githubusercontent.com/mozilla/Fira/master/ttf/FiraSans-Regular.ttf",
  "https://fonts.gstatic.com/s/firasans/v17/va9E4kDNxMZdWfMOD5Vvl4jO.ttf",
];

const FIRA_SANS_BOLD_URLS = [
  "https://www.hum-id.com/new/FiraSans-Bold.ttf",
  "https://raw.githubusercontent.com/mozilla/Fira/master/ttf/FiraSans-Bold.ttf",
  "https://fonts.gstatic.com/s/firasans/v17/va9B4kDNxMZdWfMOD5VnLK3eQRhf.ttf",
];

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const fetchAsDataUrl = async (url) => {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) throw new Error(`${url} konnte nicht geladen werden`);
  const blob = await response.blob();
  return blobToDataUrl(blob);
};

const fetchAsBase64 = async (url) => {
  const dataUrl = await fetchAsDataUrl(url);
  return dataUrl.split(",")[1];
};

const loadFirstDataUrl = async (urls) => {
  for (const url of urls) {
    try {
      return await fetchAsDataUrl(url);
    } catch (error) {
      console.warn("PDF-Asset konnte nicht geladen werden:", url, error);
    }
  }
  return null;
};

const loadFirstBase64 = async (urls) => {
  for (const url of urls) {
    try {
      return await fetchAsBase64(url);
    } catch (error) {
      console.warn("PDF-Schrift konnte nicht geladen werden:", url, error);
    }
  }
  return null;
};

/* ──────────────────────────────────────────────────────────────
 * FEATURE FLAGS
 * Auf `false` setzen, um ein Feature im Frontend auszublenden.
 * ────────────────────────────────────────────────────────────── */
const AURA_FEATURE_ENABLED = true;

/* ──────────────────────────────────────────────────────────────
 * DRUCK & EXPORT (Schritt 6, v41)
 * Markenfarben gelten nur für UI-Chrome und PDF-Layout – die funktionalen
 * SENSOR_COLORS bleiben unverändert.
 * ──────────────────────────────────────────────────────────── */
const HUMID_BRAND_RGB = {
  blue: [15, 161, 216], // #0FA1D8
  green: [166, 198, 59], // #A6C63B
  orange: [245, 137, 31], // #F5891F
  ink: [38, 38, 38], // #262626
};

const PDF_PLAN_JPEG_QUALITY = 0.85;
const COVER_BLOCK_MIN_SIZE = 24;
const STEP_LABELS = [
  "Plan",
  "Maßstab",
  "Fläche",
  "Raster",
  "Feinschliff",
  "Druck",
];

const pad2 = (n) => String(n).padStart(2, "0");

const todayGermanDate = () => {
  const now = new Date();
  return `${pad2(now.getDate())}.${pad2(
    now.getMonth() + 1
  )}.${now.getFullYear()}`;
};

const formatTimestampDe = () => {
  const now = new Date();
  return `${pad2(now.getDate())}.${pad2(
    now.getMonth() + 1
  )}.${now.getFullYear()}, ${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
};

const PDF_PRODUCT_CATALOG = [
  {
    id: "wd1",
    label: "Sensor WD1",
    fullLabel: "HUM-ID Sensor WD1",
    url: "https://www.hum-id.com/tool/assets/produkt-wd1.jpg",
  },
  {
    id: "kd1",
    label: "Sensor KD1",
    fullLabel: "HUM-ID Sensor KD1",
    url: "https://www.hum-id.com/tool/assets/produkt-kd1.jpg",
  },
  {
    id: "exa81",
    label: "Lesegerät HD EXA81",
    fullLabel: "HUM-ID Lesegerät HD EXA81",
    url: "https://www.hum-id.com/tool/assets/produkt-hdexa81.jpg",
  },
  {
    id: "ts",
    label: "Zubehör HUM-ID TS",
    fullLabel: "HUM-ID Zubehör Lesegerät HUM-ID TS",
    url: "https://www.hum-id.com/tool/assets/produkt-hum-id-ts.jpg",
  },
  {
    id: "auraSensor",
    label: "AURA Messpunkt",
    fullLabel: "HUM-ID AURA Messpunkt",
    url: "https://www.hum-id.com/tool/assets/produkt-aura.jpg",
  },
  {
    id: "auraGateway",
    label: "AURA Gateway",
    fullLabel: "HUM-ID AURA Gateway",
    url: "https://www.hum-id.com/tool/assets/produkt-aura-gateway.jpg",
  },
  {
    id: "auraConnect",
    label: "AURA Connect",
    fullLabel: "HUM-ID AURA Connect",
    url: "https://www.hum-id.com/tool/assets/produkt-aura-connect.jpg",
  },
];

/* Standard-Produktleiste: bewusst ohne Automatik – der Nutzer entscheidet. */
const DEFAULT_PRINT_PRODUCTS = ["wd1", "exa81"];

const DEFAULT_PRINT_FIELDS = () => ({
  title: "",
  subtitle: "",
  author: "",
  date: todayGermanDate(),
  version: "1.0",
  /* v43: Kennzahlen-Texte in der Fußzeile sind editierbar; leere Felder
     fallen auf die berechneten Werte zurück. */
  statsArea: "",
  statsRaster: "",
});

const DEFAULT_PRINT_SHOW = () => ({
  header: true,
  products: true,
  logo: true,
  stats: true,
});

const safePdfFileName = (name) =>
  safeProjectFileName(name).replace(/\.humidplan$/, ".pdf");

/* ──────────────────────────────────────────────────────────────
 * PERFORMANCE-RENDERING
 * Viele Sensorpunkte werden nicht mehr als tausende einzelne React-/Konva-
 * Elemente erzeugt, sondern in wenigen gebündelten Shapes gezeichnet.
 * Nur ausgewählte Sensoren bleiben als einzelne interaktive Objekte erhalten.
 * ────────────────────────────────────────────────────────────── */
const SENSOR_COLORS = {
  green: "#A5C418",
  orange: "#EA5E2A",
  blue: "#32B0FF",
};
const SENSOR_COLOR = SENSOR_COLORS.green;

const AURA_COLORS = {
  blue: "#00B2FF",
  green: "#A5C418",
  orange: "#EA5E2A",
  dark: "#111827",
};
const AURA_COLOR_OPTIONS = [
  { value: AURA_COLORS.blue, label: "Blau" },
  { value: AURA_COLORS.green, label: "Grün" },
  { value: AURA_COLORS.orange, label: "Orange" },
  { value: AURA_COLORS.dark, label: "Dunkel" },
];
const DEFAULT_AURA_COLOR = AURA_COLORS.blue;

const AREA_OVERLAY_OPTIONS = [
  { value: "green", label: "Grün", color: "#A6C63B" },
  { value: "dark", label: "Dunkel", color: "#000000" },
  { value: "white", label: "Weiss", color: "#FFFFFF" },
  { value: "none", label: "Ohne", color: null },
];
const AREA_OVERLAY_COLORS = AREA_OVERLAY_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.color;
  return acc;
}, {});
const DEFAULT_AREA_OVERLAY_MODE = "green";
const DEFAULT_AREA_OVERLAY_TRANSPARENCY = 50;

const clampAreaOverlayTransparency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_AREA_OVERLAY_TRANSPARENCY;
  return Math.min(100, Math.max(0, Math.round(n)));
};

const normalizeAreaOverlayMode = (value) =>
  AREA_OVERLAY_OPTIONS.some((opt) => opt.value === value)
    ? value
    : DEFAULT_AREA_OVERLAY_MODE;

const hexToRgba = (hex, alpha) => {
  const clean = String(hex || "").replace("#", "");
  if (clean.length !== 6) return `rgba(166,198,59,${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const getAreaOverlayFill = (mode, transparency) => {
  const normalizedMode = normalizeAreaOverlayMode(mode);
  const color = AREA_OVERLAY_COLORS[normalizedMode];
  if (!color) return null;
  const alpha = 1 - clampAreaOverlayTransparency(transparency) / 100;
  if (alpha <= 0) return null;
  return hexToRgba(color, Number(alpha.toFixed(2)));
};
const SENSOR_OUTER_RADIUS = 10;
const SENSOR_INNER_RADIUS = 4.5;
const SENSOR_HIT_RADIUS = 14;
const SENSOR_VISUAL_SCALE_MIN = 0.5;
const SENSOR_VISUAL_SCALE_MAX = 1.5;
const SENSOR_VISUAL_SCALE_STEP = 0.05;
const AURA_VISUAL_SCALE_MIN = 0.5;
const AURA_VISUAL_SCALE_MAX = 1.5;
const AURA_VISUAL_SCALE_STEP = 0.05;

// v29 Fix: +100-Blöcke verwenden exakt die Meter-Abstände der Rasterlogik; Klickpunkt ist der erste Sensor oben links.
// Werte sind screenbasiert und werden durch stageScale in Bildkoordinaten
// umgerechnet, damit die Box auch im Zoom-out klar sichtbar bleibt.
const SELECTION_BOX_COLOR = "#0066ff";
const SELECTION_BOX_PADDING_SCREEN = 14;
const SELECTION_BOX_STROKE_SCREEN = 2;
const SELECTION_BOX_HANDLE_SCREEN = 10;
const SELECTION_BOX_DASH_SCREEN = [9, 6];
const SELECTED_SENSOR_FRAME_COLOR = "#ffffff";
const SELECTED_SENSOR_FRAME_STROKE_SCREEN = 1.1;
const ADD_BLOCK_COLS = 10;
const ADD_BLOCK_ROWS = 10;
const ADD_BLOCK_LABELS = {
  single: "Einzelsensor",
  "block-1x1": "+100 (1x1)",
  "block-1x2": "+100 (1x2)",
};

const SENSOR_RASTER_OPTIONS = [
  { value: "1", label: "1 Sensor / m²" },
  { value: "2", label: "1 Sensor / 2 m²" },
  { value: "4", label: "1 Sensor / 4 m²" },
];

const SENSOR_RASTER_LABELS = SENSOR_RASTER_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const getSensorRasterLabel = (value) =>
  SENSOR_RASTER_LABELS[String(value)] || `1 Sensor pro ${value} m²`;

const getSensorRasterExportLabel = (value) => `1 Sensor pro ${value} m²`;

/* ──────────────────────────────────────────────────────────────
 * PROJEKTDATEI / AUTOSAVE
 * .humidplan ist absichtlich JSON-basiert: einfach zu debuggen,
 * sandbox.io-freundlich und später gut migrierbar.
 * ────────────────────────────────────────────────────────────── */
const PROJECT_SCHEMA = "humid-sensor-plan";
const PROJECT_FILE_VERSION = 2;
const AUTOSAVE_DB_NAME = "humid-sensorplan-db";
const AUTOSAVE_STORE_NAME = "projects";
const AUTOSAVE_KEY = "last-autosave";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImageFromSrc = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const safeProjectFileName = (name) => {
  const base = (name || "humid-sensorplan")
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-zA-Z0-9äöüÄÖÜß_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 70);
  return `${base || "humid-sensorplan"}.humidplan`;
};

const safeDxfFileName = (name) =>
  safeProjectFileName(name).replace(/\.humidplan$/, ".dxf");

const downloadTextFile = (fileName, text, mimeType = "application/json") => {
  const blob = new Blob([text], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const openAutosaveDb = () =>
  new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error("IndexedDB wird von diesem Browser nicht unterstützt."));
      return;
    }

    const request = window.indexedDB.open(AUTOSAVE_DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(AUTOSAVE_STORE_NAME)) {
        db.createObjectStore(AUTOSAVE_STORE_NAME, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const saveAutosaveProject = async (project) => {
  const db = await openAutosaveDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUTOSAVE_STORE_NAME, "readwrite");
    tx.objectStore(AUTOSAVE_STORE_NAME).put({
      id: AUTOSAVE_KEY,
      updatedAt: new Date().toISOString(),
      project,
    });
    tx.oncomplete = () => {
      db.close();
      resolve(true);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
};

const loadAutosaveProject = async () => {
  const db = await openAutosaveDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(AUTOSAVE_STORE_NAME, "readonly");
    const request = tx.objectStore(AUTOSAVE_STORE_NAME).get(AUTOSAVE_KEY);
    request.onsuccess = () => {
      db.close();
      resolve(request.result || null);
    };
    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
};

/* ---- SVG‑Platzhalter (Info‑Icon) ---- */
const InfoIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="12" r="11" stroke="white" strokeWidth="2" fill="none" />
    <line x1="12" y1="8" x2="12" y2="13" stroke="white" strokeWidth="2" />
    <circle cx="12" cy="17" r="1" fill="white" />
  </svg>
);

/* ---- Wiederverwendbarer Button ----
   v41: auf Modulebene verschoben, damit die Komponente nicht bei jedem
   Render neu erzeugt wird (stabile Identität, weniger Re-Mounts). */
const Btn = ({ children, variant = "default", className = "", ...props }) => (
  <button
    className={`btn ${variant !== "default" ? variant : ""} ${className}`}
    {...props}
  >
    {children}
  </button>
);

export default function SensorKalkulationDummy() {
  /* -------------------- State -------------------- */
  const containerRef = useRef(null);
  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState("");
  const [projectName, setProjectName] = useState("Unbenanntes Projekt");
  const [backgroundDataUrl, setBackgroundDataUrl] = useState(null);
  const [imageObj, setImageObj] = useState(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const projectInputRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const restoringProjectRef = useRef(false);
  const [autosaveAvailable, setAutosaveAvailable] = useState(false);
  const [autosaveLastSaved, setAutosaveLastSaved] = useState(null);
  const [projectNotice, setProjectNotice] = useState("");
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showStep5Info, setShowStep5Info] = useState(false); // Info-Popover für Schritt 5
  const handleCloseOfferModal = () => {
    setShowOfferModal(false);
  };

  const [showPolygonHelpPopover, setShowPolygonHelpPopover] = useState(false);
  const [dontShowPolygonHelpAgain, setDontShowPolygonHelpAgain] = useState(
    localStorage.getItem("dontShowPolygonHelpAgain") === "true"
  );

  const restrictDragBounds = (pos) => {
    let newX = pos.x;
    let newY = pos.y;

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 0;
    const stageWrapper = containerRef.current;
    const containerHeight = stageWrapper ? stageWrapper.offsetHeight : 0;

    if (!imageObj || containerWidth === 0 || containerHeight === 0) {
      return pos;
    }

    newY = Math.min(newY, 0);
    newX = Math.min(newX, 0);

    const scaledImageHeight = imageObj.height * stageScale;
    if (scaledImageHeight > containerHeight) {
      newY = Math.max(newY, containerHeight - scaledImageHeight);
    }

    const scaledImageWidth = imageObj.width * stageScale;
    if (scaledImageWidth > containerWidth) {
      newX = Math.max(newX, containerWidth - scaledImageWidth);
    }

    return { x: newX, y: newY };
  };

  const [mPerPx, setMPerPx] = useState(null);
  const [realDistance, setRealDistance] = useState("");
  const [scalePoints, setScalePoints] = useState([]);

  const [polygonPoints, setPolygonPoints] = useState([]);
  const [polyClosed, setPolyClosed] = useState(false);
  const [areaM2, setAreaM2] = useState(null);
  const [sensorRaster, setSensorRaster] = useState("1");
  const [sensorCount, setSensorCount] = useState(null);
  const [sensorPoints, setSensorPoints] = useState([]);
  const [sensorVisualScale, setSensorVisualScale] = useState(1);
  const [areaOverlayMode, setAreaOverlayMode] = useState(
    DEFAULT_AREA_OVERLAY_MODE
  );
  const [areaOverlayTransparency, setAreaOverlayTransparency] = useState(
    DEFAULT_AREA_OVERLAY_TRANSPARENCY
  );

  const [stageScale, setStageScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const stageRef = useRef(null);
  const selectionMarqueeRef = useRef(null);
  const [history, setHistory] = useState([]);

  // Raster‑Drag
  const [rasterOffset, setRasterOffset] = useState({ x: 0, y: 0 });
  const [rasterDraggable, setRasterDraggable] = useState(false);

  // Sensor‑Delete‑Modus
  const [deleteMode, setDeleteMode] = useState(false);
  const [deletedIds, setDeletedIds] = useState([]);

  // Punkte‑Hinzufügen
  const [addMode, setAddMode] = useState(false);
  const [addPlacementMode, setAddPlacementMode] = useState("single");
  const [addedPoints, setAddedPoints] = useState([]);

  // Mehrfach‑Löschen (Bereich)
  const [areaDeleteMode, setAreaDeleteMode] = useState(false);
  const [selRect, setSelRect] = useState(null);

  // Mehrfach‑Auswahl & Verschieben
  const [moveSelectionMode, setMoveSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sensorOffsets, setSensorOffsets] = useState({});
  const [sensorColors, setSensorColors] = useState({});

  // Aura (blaue Reichweite, 10 m)
  const [auraMode, setAuraMode] = useState(false);
  const [auraPoints, setAuraPoints] = useState([]);
  const [auraVisualScale, setAuraVisualScale] = useState(1);
  const [auraNumbersEnabled, setAuraNumbersEnabled] = useState(false);

  /* Schritt 6 – Druck & Export (v41) */
  const [printFields, setPrintFields] = useState(DEFAULT_PRINT_FIELDS);
  const [printShow, setPrintShow] = useState(DEFAULT_PRINT_SHOW);
  const [printProducts, setPrintProducts] = useState(() => [
    ...DEFAULT_PRINT_PRODUCTS,
  ]);
  const [coverBlocks, setCoverBlocks] = useState([]);
  const [selectedCoverId, setSelectedCoverId] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isPdfBusy, setIsPdfBusy] = useState(false);
  const [printProductCounts, setPrintProductCounts] = useState({});
  const [printPanelPos, setPrintPanelPos] = useState(null);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const headerRef = useRef(null);
  const uiLayerRef = useRef(null);
  const pdfAssetCacheRef = useRef({});
  const coverNodeRefs = useRef({}); // id -> Konva.Rect (weiße Fläche)
  const coverUiRefs = useRef({ outline: null, handles: {} });
  const coverGestureRef = useRef(null); // Box beim Start einer Griff-Geste
  const printLayoutRef = useRef(null);
  const printPanelRef = useRef(null);

  // ────────────────── Kontextmenü & Kopieren/Drehen ──────────────────
  const [contextMenu, setContextMenu] = useState(null); // {screenX, screenY, imgX, imgY}
  const [clipboard, setClipboard] = useState(null); // [{x,y}, ...] relative Punkte
  const [clipboardCenter, setClipboardCenter] = useState(null); // letzter absoluter Mittelpunkt der kopierten Gruppe
  const [pasteMode, setPasteMode] = useState(false); // Warte auf Rechtsklick zum Einfügen
  const [rotateMode, setRotateMode] = useState(false); // Dreh-Modus aktiv
  const [rotateAngle, setRotateAngle] = useState(0); // aktueller Winkel in Rad
  const [rotateCenter, setRotateCenter] = useState(null); // {x,y} Mittelpunkt

  /* ────────── Projekt speichern / laden / Autosave ────────── */
  const currentSensorCount =
    sensorPoints.length + addedPoints.length - deletedIds.length;

  /* ────────── Sensor-Datenmodell für performantes Rendering ────────── */
  const deletedIdSet = useMemo(() => new Set(deletedIds), [deletedIds]);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const allSensorItems = useMemo(
    () => [
      ...sensorPoints.map((p, i) => ({ ...p, id: `s-${i}` })),
      ...addedPoints.map((p, i) => ({ ...p, id: `a-${i}` })),
    ],
    [sensorPoints, addedPoints]
  );

  const activeSensorItems = useMemo(
    () => allSensorItems.filter((pt) => !deletedIdSet.has(pt.id)),
    [allSensorItems, deletedIdSet]
  );

  const bulkSensorItems = useMemo(
    () => activeSensorItems.filter((pt) => !selectedIdSet.has(pt.id)),
    [activeSensorItems, selectedIdSet]
  );

  const selectedSensorItems = useMemo(
    () => activeSensorItems.filter((pt) => selectedIdSet.has(pt.id)),
    [activeSensorItems, selectedIdSet]
  );

  const getSensorColor = (id) => sensorColors[id] || SENSOR_COLOR;
  const getAuraColor = (aura) => aura?.color || DEFAULT_AURA_COLOR;
  const areaOverlayFill = getAreaOverlayFill(
    areaOverlayMode,
    areaOverlayTransparency
  );

  const safeSensorVisualScale = Math.min(
    SENSOR_VISUAL_SCALE_MAX,
    Math.max(SENSOR_VISUAL_SCALE_MIN, Number(sensorVisualScale) || 1)
  );
  const sensorOuterRadius = SENSOR_OUTER_RADIUS * safeSensorVisualScale;
  const sensorInnerRadius = SENSOR_INNER_RADIUS * safeSensorVisualScale;
  const sensorHitRadius = SENSOR_HIT_RADIUS * safeSensorVisualScale;
  const safeAuraVisualScale = Math.min(
    AURA_VISUAL_SCALE_MAX,
    Math.max(AURA_VISUAL_SCALE_MIN, Number(auraVisualScale) || 1)
  );

  const applyColorToSelection = (color) => {
    if (!selectedIds.length) return;
    const normalizedColor = color === SENSOR_COLOR ? null : color;
    const prevColors = { ...sensorColors };

    setSensorColors((prev) => {
      const nxt = { ...prev };
      selectedIds.forEach((id) => {
        if (normalizedColor) nxt[id] = normalizedColor;
        else delete nxt[id];
      });
      return nxt;
    });

    setHistory((h) => [
      ...h,
      { type: "color", ids: [...selectedIds], prevColors },
    ]);
    setContextMenu(null);
  };

  const buildProjectSnapshot = () => ({
    schema: PROJECT_SCHEMA,
    version: PROJECT_FILE_VERSION,
    appVersion: "v47",
    savedAt: new Date().toISOString(),
    projectName,
    fileName,
    backgroundDataUrl,
    workflow: {
      step,
    },
    calibration: {
      mPerPx,
      realDistance,
      scalePoints,
    },
    area: {
      polygonPoints,
      polyClosed,
      areaM2,
    },
    grid: {
      sensorRaster,
      sensorCount,
      sensorPoints,
      rasterOffset,
    },
    edits: {
      deletedIds,
      addedPoints,
      sensorOffsets,
      sensorColors,
    },
    aura: {
      enabled: AURA_FEATURE_ENABLED,
      auraPoints,
      visualScale: safeAuraVisualScale,
      numbersEnabled: auraNumbersEnabled,
    },
    print: {
      fields: { ...printFields },
      show: { ...printShow },
      products: [...printProducts],
      productCounts: { ...printProductCounts },
      coverBlocks: coverBlocks.map((b) => ({ ...b })),
    },
    view: {
      stageScale,
      stagePos,
      sensorVisualScale: safeSensorVisualScale,
      areaOverlayMode,
      areaOverlayTransparency: clampAreaOverlayTransparency(
        areaOverlayTransparency
      ),
      areaOverlayHidden: areaOverlayMode === "none",
    },
    stats: {
      currentSensorCount,
    },
  });

  const applyProjectSnapshot = async (project, sourceLabel = "Projekt") => {
    if (!project || project.schema !== PROJECT_SCHEMA) {
      throw new Error("Diese Datei ist keine gültige HUM-ID Projektdatei.");
    }
    if (!project.backgroundDataUrl) {
      throw new Error("Die Projektdatei enthält keine Planunterlage.");
    }

    restoringProjectRef.current = true;
    const loadedImg = await loadImageFromSrc(project.backgroundDataUrl);

    setProjectName(project.projectName || "Unbenanntes Projekt");
    setFileName(project.fileName || "Projektdatei");
    setBackgroundDataUrl(project.backgroundDataUrl);
    setImageObj(loadedImg);

    const calibration = project.calibration || {};
    setMPerPx(calibration.mPerPx ?? null);
    setRealDistance(calibration.realDistance || "");
    setScalePoints(calibration.scalePoints || []);

    const area = project.area || {};
    setPolygonPoints(area.polygonPoints || []);
    setPolyClosed(Boolean(area.polyClosed));
    setAreaM2(area.areaM2 ?? null);

    const grid = project.grid || {};
    const loadedRaster = String(grid.sensorRaster || "1");
    setSensorRaster(SENSOR_RASTER_LABELS[loadedRaster] ? loadedRaster : "1");
    setSensorCount(grid.sensorCount ?? null);
    setSensorPoints(grid.sensorPoints || []);
    setRasterOffset(grid.rasterOffset || { x: 0, y: 0 });

    const edits = project.edits || {};
    setDeletedIds(edits.deletedIds || []);
    setAddedPoints(edits.addedPoints || []);
    setSensorOffsets(edits.sensorOffsets || {});
    setSensorColors(edits.sensorColors || {});

    const aura = project.aura || {};
    setAuraPoints(aura.auraPoints || []);
    const loadedAuraVisualScale = Number(aura.visualScale);
    setAuraVisualScale(
      Number.isFinite(loadedAuraVisualScale)
        ? Math.min(
            AURA_VISUAL_SCALE_MAX,
            Math.max(AURA_VISUAL_SCALE_MIN, loadedAuraVisualScale)
          )
        : 1
    );
    setAuraNumbersEnabled(Boolean(aura.numbersEnabled));

    /* v41: Druck-Einstellungen wiederherstellen. Ältere Projektdateien
       (Version 1) haben keinen print-Block und erhalten Standardwerte. */
    const print = project.print || {};
    setPrintFields({ ...DEFAULT_PRINT_FIELDS(), ...(print.fields || {}) });
    setPrintShow({ ...DEFAULT_PRINT_SHOW(), ...(print.show || {}) });
    setPrintProducts(
      Array.isArray(print.products)
        ? PDF_PRODUCT_CATALOG.filter((p) => print.products.includes(p.id)).map(
            (p) => p.id
          )
        : [...DEFAULT_PRINT_PRODUCTS]
    );
    setPrintProductCounts(
      print.productCounts && typeof print.productCounts === "object"
        ? { ...print.productCounts }
        : {}
    );
    setCoverBlocks(
      Array.isArray(print.coverBlocks)
        ? print.coverBlocks.map((b) => ({ ...b }))
        : []
    );
    setSelectedCoverId(null);
    setPrintPanelPos(null);

    const nextStep = Math.min(Math.max(project.workflow?.step || 5, 1), 6);
    setStep(nextStep);

    // Transiente Bearbeitungsmodi beim Laden bewusst deaktivieren.
    setRasterDraggable(false);
    setDeleteMode(false);
    setAddMode(false);
    setAddPlacementMode("single");
    setAreaDeleteMode(false);
    setSelRect(null);
    setMoveSelectionMode(false);
    setSelectedIds([]);
    setAuraMode(false);
    setContextMenu(null);
    setClipboard(null);
    setPasteMode(false);
    setRotateMode(false);
    setRotateAngle(0);
    setRotateCenter(null);
    setShowStep5Info(false);
    setHistory([]);

    const view = project.view || {};
    if (view.stageScale && view.stagePos) {
      setStageScale(view.stageScale);
      setStagePos(view.stagePos);
    }
    const loadedSensorVisualScale = Number(view.sensorVisualScale);
    setSensorVisualScale(
      Number.isFinite(loadedSensorVisualScale)
        ? Math.min(
            SENSOR_VISUAL_SCALE_MAX,
            Math.max(SENSOR_VISUAL_SCALE_MIN, loadedSensorVisualScale)
          )
        : 1
    );
    const legacyAreaOverlayMode = view.areaOverlayHidden ? "none" : "green";
    setAreaOverlayMode(
      normalizeAreaOverlayMode(view.areaOverlayMode || legacyAreaOverlayMode)
    );
    setAreaOverlayTransparency(
      clampAreaOverlayTransparency(
        view.areaOverlayTransparency ?? DEFAULT_AREA_OVERLAY_TRANSPARENCY
      )
    );

    setProjectNotice(`${sourceLabel} geladen.`);
    window.setTimeout(() => {
      restoringProjectRef.current = false;
    }, 0);
  };

  const handleSaveProject = () => {
    if (!backgroundDataUrl) {
      setProjectNotice("Bitte erst eine Planunterlage laden.");
      return;
    }
    const snapshot = buildProjectSnapshot();
    const suggestedName = projectName || fileName || "humid-sensorplan";
    downloadTextFile(
      safeProjectFileName(suggestedName),
      JSON.stringify(snapshot, null, 2)
    );
    setProjectNotice("Projektdatei wurde heruntergeladen.");
  };

  const handleProjectFileOpen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const project = JSON.parse(text);
      await applyProjectSnapshot(project, file.name);
      setAutosaveAvailable(true);
    } catch (error) {
      console.error(error);
      setProjectNotice(
        error.message || "Projektdatei konnte nicht geladen werden."
      );
    } finally {
      if (projectInputRef.current) projectInputRef.current.value = "";
    }
  };

  const handleRestoreAutosave = async () => {
    try {
      const record = await loadAutosaveProject();
      if (!record?.project) {
        setProjectNotice("Keine lokale Auto-Sicherung gefunden.");
        setAutosaveAvailable(false);
        return;
      }
      await applyProjectSnapshot(record.project, "Auto-Sicherung");
      setAutosaveLastSaved(record.updatedAt || null);
    } catch (error) {
      console.error(error);
      setProjectNotice("Auto-Sicherung konnte nicht geladen werden.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    loadAutosaveProject()
      .then((record) => {
        if (cancelled) return;
        setAutosaveAvailable(Boolean(record?.project));
        setAutosaveLastSaved(record?.updatedAt || null);
      })
      .catch(() => {
        if (!cancelled) setAutosaveAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!backgroundDataUrl || restoringProjectRef.current) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    autosaveTimerRef.current = window.setTimeout(() => {
      const snapshot = buildProjectSnapshot();
      saveAutosaveProject(snapshot)
        .then(() => {
          const now = new Date().toISOString();
          setAutosaveLastSaved(now);
          setAutosaveAvailable(true);
        })
        .catch((error) => {
          console.warn("Autosave fehlgeschlagen:", error);
        });
    }, 2000);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [
    projectName,
    fileName,
    backgroundDataUrl,
    step,
    mPerPx,
    realDistance,
    scalePoints,
    polygonPoints,
    polyClosed,
    areaM2,
    sensorRaster,
    sensorCount,
    sensorPoints,
    rasterOffset,
    deletedIds,
    addedPoints,
    sensorOffsets,
    sensorColors,
    sensorVisualScale,
    auraVisualScale,
    auraNumbersEnabled,
    areaOverlayMode,
    areaOverlayTransparency,
    auraPoints,
    printFields,
    printShow,
    printProducts,
    printProductCounts,
    coverBlocks,
  ]);

  /* v45: Die Kennzahlen in der PDF-Fußzeile werden nicht mehr automatisch
     vorbelegt. Im Rahmen-Layout erscheint dort nur, was der Nutzer in
     „PDF gestalten“ selbst einträgt. */

  /* v43: --header-total folgt der tatsächlichen Header-Höhe – damit
     funktioniert auch der eingeklappte Zustand ohne Magic Numbers. */
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () =>
      document.documentElement.style.setProperty(
        "--header-total",
        `${el.offsetHeight}px`
      );
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [headerCollapsed, step]);

  const autosaveTimeLabel = autosaveLastSaved
    ? new Date(autosaveLastSaved).toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  /* ────────── Animierte Auswahl-Box („marching ants“) ────────── */
  useEffect(() => {
    if (!moveSelectionMode || selectedIds.length === 0) return;

    let rafId = null;
    const speed = 0.055; // px/ms in Bildschirm-Anmutung; dezent, aber sichtbar

    const tick = (now) => {
      const node = selectionMarqueeRef.current;
      if (node) {
        const safeScale = Math.max(stageScale, 0.001);
        const cycle =
          (SELECTION_BOX_DASH_SCREEN[0] + SELECTION_BOX_DASH_SCREEN[1]) /
          safeScale;
        node.dashOffset(-((now * speed) % Math.max(cycle, 1)));
        const layer = node.getLayer();
        if (layer) layer.batchDraw();
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [moveSelectionMode, selectedIds.length, stageScale]);

  /* ────────── Undo-History (nur Schritt 5) ────────── */

  const undoLastAction = () => {
    if (history.length === 0) return;
    const last = history[history.length - 1];

    setHistory((h) => h.slice(0, -1));

    switch (last.type) {
      case "drag":
        setRasterOffset(last.prevOffset);
        break;

      case "add": {
        const count = last.count || 1;
        setAddedPoints((pts) => pts.slice(0, -count));
        if (last.idsAdded) {
          setSelectedIds((ids) =>
            ids.filter((id) => !last.idsAdded.includes(id))
          );
          setSensorColors((prev) => {
            const nxt = { ...prev };
            last.idsAdded.forEach((id) => delete nxt[id]);
            return nxt;
          });
        }
        break;
      }

      case "delete":
        setDeletedIds((ids) =>
          ids.filter((id) => !last.idsDeleted.includes(id))
        );
        break;

      case "moveSelection":
        setSensorOffsets((prev) => {
          const next = { ...prev };
          last.ids.forEach((id) => {
            const cur = next[id] || { x: 0, y: 0 };
            next[id] = { x: cur.x - last.dx, y: cur.y - last.dy };
          });
          return next;
        });
        break;

      case "setOffsets":
        setSensorOffsets((prev) => {
          const nxt = { ...prev };
          last.ids.forEach((id) => {
            nxt[id] = last.prevOffsets[id] || { x: 0, y: 0 };
          });
          return nxt;
        });
        break;

      case "auraAdd":
        setAuraPoints((pts) => pts.filter((a) => a.id !== last.aura.id));
        break;

      case "auraDelete":
        setAuraPoints((pts) => [...pts, last.aura]);
        break;

      case "auraMove":
        setAuraPoints((pts) =>
          pts.map((p) =>
            p.id === last.id ? { ...p, x: last.prevX, y: last.prevY } : p
          )
        );
        break;

      case "auraColor":
        setAuraPoints((pts) =>
          pts.map((p) => {
            if (p.id !== last.id) return p;
            if (last.prevColor) return { ...p, color: last.prevColor };
            const { color, ...rest } = p;
            return rest;
          })
        );
        break;

      case "paste":
        setAddedPoints((pts) => pts.slice(0, -last.count));
        if (last.idsAdded) {
          setSelectedIds((ids) =>
            ids.filter((id) => !last.idsAdded.includes(id))
          );
          setSensorColors((prev) => {
            const nxt = { ...prev };
            last.idsAdded.forEach((id) => delete nxt[id]);
            return nxt;
          });
        }
        break;

      case "color":
        setSensorColors(last.prevColors || {});
        break;

      case "rotate":
        setSensorOffsets((prev) => {
          const nxt = { ...prev };
          last.ids.forEach((id) => {
            nxt[id] = last.prevOffsets[id] || { x: 0, y: 0 };
          });
          return nxt;
        });
        break;

      case "coverAdd":
        setCoverBlocks((blocks) =>
          blocks.filter((b) => b.id !== last.block.id)
        );
        setSelectedCoverId((sel) => (sel === last.block.id ? null : sel));
        break;

      case "coverRemove":
        setCoverBlocks((blocks) => [...blocks, { ...last.block }]);
        break;

      case "coverChange":
        setCoverBlocks((blocks) =>
          blocks.map((b) => (b.id === last.block.id ? { ...last.block } : b))
        );
        break;

      default:
        console.warn("Unbekannter Undo-Typ:", last);
    }
  };

  /* ---------- Schritt‑bezogene Info‑Texte ---------- */
  const stepHelp = {
    1: "Wählen Sie eine PDF‑, PNG‑ oder JPG‑Datei (Dachaufsicht o. ä.) aus.",
    2: "Klicken Sie zwei Punkte eines bekannten Maßstabs an und geben Sie die Distanz ein.",
    3: "Klicken Sie nacheinander die Eckpunkte der Fläche. Mit Klick auf den ersten Punkt schließen Sie das Polygon.",
    4: "Wählen Sie das gewünschte Sensor‑Raster. Empfehlung: 1×1 m bei < 200 m² …",
    5: "Raster verschieben, Sensoren löschen/hinzufügen. Anschließend PDF herunterladen oder gestalten.",
    6: "Projektangaben, Produktübersicht und Abdeckblöcke für den PDF-Printout festlegen.",
  };

  // Logo vorladen
  const [logoImg, setLogoImg] = useState(null);
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = `https://www.hum-id.com/new/logo-website-big-01.png`;
    img.onload = () => setLogoImg(img);
    img.onerror = () => console.warn("Logo konnte nicht vorgeladen werden");
  }, []);

  useEffect(() => {
    if (step === 3 && !dontShowPolygonHelpAgain && polygonPoints.length === 0) {
      setShowPolygonHelpPopover(true);
    } else {
      setShowPolygonHelpPopover(false);
    }
  }, [step, dontShowPolygonHelpAgain, polygonPoints.length]);

  const handleClosePolygonHelpPopover = () => {
    setShowPolygonHelpPopover(false);
  };

  const handleDontShowPolygonHelpAgain = () => {
    localStorage.setItem("dontShowPolygonHelpAgain", "true");
    setDontShowPolygonHelpAgain(true);
    setShowPolygonHelpPopover(false);
  };

  /* ---------------- Upload ---------------- */
  const handleFileUpload = async (e, options = {}) => {
    const file = e.target.files[0];
    if (!file) return;

    const isSpecialPdfUpload = Boolean(options.specialPdf);
    const isPdfFile =
      file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");

    if (isSpecialPdfUpload && !isPdfFile) {
      setProjectNotice("Upload HD ist nur für PDF-Dateien vorgesehen.");
      if (e.target) e.target.value = "";
      return;
    }

    setIsFileLoading(true);
    setFileName(file.name);
    setProjectName(file.name.replace(/\.[^/.]+$/, "") || "Unbenanntes Projekt");
    setProjectNotice("");

    try {
      let dataUrl;

      if (isPdfFile) {
        dataUrl = await pdfToPngDataUrl(file, { special: isSpecialPdfUpload });
      } else {
        dataUrl = await fileToDataUrl(file);
      }

      setBackgroundDataUrl(dataUrl);
      const img = await loadImageFromSrc(dataUrl);
      setImageObj(img);
      setProjectNotice(
        isSpecialPdfUpload
          ? "HD-PDF geladen: hohe Auflösung ohne JPEG-Kompression – ideal für den Druck, die Bearbeitung ist dafür etwas langsamer."
          : ""
      );
    } catch (error) {
      console.error(error);
      setProjectNotice("Die Datei konnte nicht geladen werden.");
      setFileName("");
      setBackgroundDataUrl(null);
      setImageObj(null);
    } finally {
      setIsFileLoading(false);
    }
  };

  /* ------------- Zoom / Pan -------------- */
  const toImage = (pos) => ({
    x: (pos.x - stagePos.x) / stageScale,
    y: (pos.y - stagePos.y) / stageScale,
  });

  const findNearestSensorId = (imgPos) => {
    // Toleranz bleibt in Bildkoordinaten stabil, wird bei sehr starkem
    // Herauszoomen aber etwas größer, damit Sensoren noch klickbar bleiben.
    const hitRadius = Math.min(
      40,
      Math.max(sensorHitRadius, 8 / Math.max(stageScale, 0.001))
    );
    const hitRadiusSq = hitRadius * hitRadius;
    let bestId = null;
    let bestDistSq = hitRadiusSq;

    for (const pt of activeSensorItems) {
      const off = sensorOffsets[pt.id] || { x: 0, y: 0 };
      const dx = pt.x + rasterOffset.x + off.x - imgPos.x;
      const dy = pt.y + rasterOffset.y + off.y - imgPos.y;
      const distSq = dx * dx + dy * dy;
      if (distSq <= bestDistSq) {
        bestDistSq = distSq;
        bestId = pt.id;
      }
    }

    return bestId;
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const old = stageScale;
    const stage = e.target.getStage();
    const ptr = stage.getPointerPosition();
    const mouse = {
      x: (ptr.x - stagePos.x) / old,
      y: (ptr.y - stagePos.y) / old,
    };
    const newScale = e.evt.deltaY > 0 ? old / scaleBy : old * scaleBy;
    setStageScale(newScale);
    setStagePos({
      x: ptr.x - mouse.x * newScale,
      y: ptr.y - mouse.y * newScale,
    });
  };

  /* ────────── Kontextmenü: Kopieren / Drehen / Löschen ────────── */

  // Zentrum einer Punkt-Gruppe berechnen
  const getCenterOfPoints = (pts) => {
    if (!pts.length) return { x: 0, y: 0 };
    const sumX = pts.reduce((s, p) => s + p.x, 0);
    const sumY = pts.reduce((s, p) => s + p.y, 0);
    return { x: sumX / pts.length, y: sumY / pts.length };
  };

  const getSensorBasePointById = (id) =>
    id?.startsWith("s-")
      ? sensorPoints[+id.slice(2)]
      : id?.startsWith("a-")
      ? addedPoints[+id.slice(2)]
      : null;

  const getSensorAbsolutePointById = (id, offsets = sensorOffsets) => {
    const base = getSensorBasePointById(id);
    if (!base || deletedIds.includes(id)) return null;
    const off = offsets[id] || { x: 0, y: 0 };
    return {
      id,
      x: base.x + rasterOffset.x + off.x,
      y: base.y + rasterOffset.y + off.y,
      color: getSensorColor(id),
    };
  };

  // Ausgewählte Sensor-Objekte (mit Offset) sammeln
  const getSelectedSensorCoords = () =>
    selectedIds.map((id) => getSensorAbsolutePointById(id)).filter(Boolean);

  const setSelectedSensorAbsolutePositions = (positionsById) => {
    const ids = selectedIds.filter((id) => positionsById[id]);
    if (!ids.length) return;

    const prevOffsets = {};
    ids.forEach((id) => {
      prevOffsets[id] = { ...(sensorOffsets[id] || { x: 0, y: 0 }) };
    });

    setSensorOffsets((prev) => {
      const nxt = { ...prev };
      ids.forEach((id) => {
        const base = getSensorBasePointById(id);
        const nextAbs = positionsById[id];
        if (!base || !nextAbs) return;
        nxt[id] = {
          x: nextAbs.x - base.x - rasterOffset.x,
          y: nextAbs.y - base.y - rasterOffset.y,
        };
      });
      return nxt;
    });

    setHistory((h) => [...h, { type: "setOffsets", ids, prevOffsets }]);
    setContextMenu(null);
  };

  const moveSelectedSensorsBy = (dx, dy) => {
    if (!selectedIds.length || (dx === 0 && dy === 0)) return;
    const ids = [...selectedIds];

    setSensorOffsets((prev) => {
      const nxt = { ...prev };
      ids.forEach((id) => {
        const cur = prev[id] || { x: 0, y: 0 };
        nxt[id] = { x: cur.x + dx, y: cur.y + dy };
      });
      return nxt;
    });

    setHistory((h) => [...h, { type: "moveSelection", ids, dx, dy }]);
    setContextMenu(null);
  };

  const handleAlignSelection = (direction) => {
    const coords = getSelectedSensorCoords();
    if (coords.length < 2) {
      setContextMenu(null);
      return;
    }

    const targetY =
      direction === "top"
        ? Math.min(...coords.map((p) => p.y))
        : Math.max(...coords.map((p) => p.y));

    const nextPositions = {};
    coords.forEach((p) => {
      nextPositions[p.id] = { x: p.x, y: targetY };
    });
    setSelectedSensorAbsolutePositions(nextPositions);
  };

  const handleDistributeSelection = () => {
    const coords = getSelectedSensorCoords();
    if (coords.length < 3) {
      setContextMenu(null);
      return;
    }

    const minX = Math.min(...coords.map((p) => p.x));
    const maxX = Math.max(...coords.map((p) => p.x));
    const minY = Math.min(...coords.map((p) => p.y));
    const maxY = Math.max(...coords.map((p) => p.y));
    const distributeHorizontally = maxX - minX >= maxY - minY;

    const sorted = [...coords].sort((a, b) =>
      distributeHorizontally ? a.x - b.x : a.y - b.y
    );
    const start = distributeHorizontally ? sorted[0].x : sorted[0].y;
    const end = distributeHorizontally
      ? sorted[sorted.length - 1].x
      : sorted[sorted.length - 1].y;
    const stepSize = (end - start) / (sorted.length - 1);

    if (!Number.isFinite(stepSize) || stepSize === 0) {
      setContextMenu(null);
      return;
    }

    const nextPositions = {};
    sorted.forEach((p, idx) => {
      if (distributeHorizontally) {
        nextPositions[p.id] = { x: start + idx * stepSize, y: p.y };
      } else {
        nextPositions[p.id] = { x: p.x, y: start + idx * stepSize };
      }
    });
    setSelectedSensorAbsolutePositions(nextPositions);
  };

  const isAddToSelectionEvent = (evt) =>
    !!(evt && (evt.shiftKey || evt.metaKey || evt.ctrlKey));

  const selectSensorByClick = (sensorId, additive = false) => {
    if (!sensorId) return;
    setSelectedIds((prev) => {
      if (additive) {
        return prev.includes(sensorId) ? prev : [...prev, sensorId];
      }
      return [sensorId];
    });
    setMoveSelectionMode(true);
    setContextMenu(null);
  };

  const copySelectionToClipboard = ({ activatePasteMode = false } = {}) => {
    const coords = getSelectedSensorCoords();
    if (!coords.length) return false;
    const center = getCenterOfPoints(coords);
    setClipboard(
      coords.map((p) => ({
        x: p.x - center.x,
        y: p.y - center.y,
        color: p.color,
      }))
    );
    setClipboardCenter(center);
    setPasteMode(activatePasteMode);
    setContextMenu(null);
    return true;
  };

  const pasteClipboardAtCenter = (center) => {
    if (!clipboard || !clipboard.length || !center) return false;

    const newPts = clipboard.map((p) => ({
      x: center.x + p.x - rasterOffset.x,
      y: center.y + p.y - rasterOffset.y,
    }));

    // Die eingefügten Punkte landen am Ende von addedPoints.
    // Daraus entstehen stabile IDs im Format a-<Index>, die wir direkt markieren.
    const firstNewIndex = addedPoints.length;
    const newIds = newPts.map((_, i) => `a-${firstNewIndex + i}`);
    const newColorEntries = {};
    clipboard.forEach((p, i) => {
      if (p.color && p.color !== SENSOR_COLOR)
        newColorEntries[newIds[i]] = p.color;
    });

    setAddedPoints((prev) => [...prev, ...newPts]);
    setSensorColors((prev) => ({ ...prev, ...newColorEntries }));
    setSelectedIds(newIds);
    setMoveSelectionMode(true);
    setHistory((h) => [
      ...h,
      { type: "paste", count: newPts.length, idsAdded: newIds },
    ]);
    setClipboardCenter(center);
    setPasteMode(false);
    setContextMenu(null);
    return true;
  };

  /* ── Kopieren ── */
  const handleCopy = () => {
    // Kontextmenü-Kopieren: bisheriger Workflow bleibt erhalten.
    // Danach kann per Linksklick an beliebiger Stelle eingefügt werden.
    copySelectionToClipboard({ activatePasteMode: true });
  };

  /* ── Einfügen an Klickposition ── */
  const handlePaste = (imgX, imgY) => {
    pasteClipboardAtCenter({ x: imgX, y: imgY });
  };

  /* ── Tastatur-Paste: wie in Zeichenprogrammen leicht versetzt einfügen ── */
  const handleKeyboardPaste = () => {
    if (!clipboard || !clipboard.length) return false;

    const coords = getSelectedSensorCoords();
    const baseCenter = coords.length
      ? getCenterOfPoints(coords)
      : clipboardCenter || {
          x: imageObj ? imageObj.width / 2 : 0,
          y: imageObj ? imageObj.height / 2 : 0,
        };

    const safeScale = Math.max(stageScale, 0.001);
    const pasteCenter = {
      x: baseCenter.x + 46 / safeScale,
      y: baseCenter.y - 46 / safeScale,
    };

    return pasteClipboardAtCenter(pasteCenter);
  };

  /* ── Duplizieren: Auswahl sofort leicht versetzt kopieren und aktiv markieren ── */
  const handleDuplicateSelected = () => {
    const coords = getSelectedSensorCoords();
    if (!coords.length) return;

    const safeScale = Math.max(stageScale, 0.001);
    const dx = 46 / safeScale;
    const dy = -46 / safeScale;

    const newPts = coords.map((p) => ({
      x: p.x + dx - rasterOffset.x,
      y: p.y + dy - rasterOffset.y,
    }));

    const firstNewIndex = addedPoints.length;
    const newIds = newPts.map((_, i) => `a-${firstNewIndex + i}`);
    const newColorEntries = {};
    coords.forEach((p, i) => {
      if (p.color && p.color !== SENSOR_COLOR)
        newColorEntries[newIds[i]] = p.color;
    });

    setAddedPoints((prev) => [...prev, ...newPts]);
    setSensorColors((prev) => ({ ...prev, ...newColorEntries }));
    setSelectedIds(newIds);
    setMoveSelectionMode(true);
    setHistory((h) => [
      ...h,
      { type: "paste", count: newPts.length, idsAdded: newIds },
    ]);
    setPasteMode(false);
    setContextMenu(null);
  };

  const createAddBlockPoints = (anchor, mode) => {
    if (!mPerPx || !Number.isFinite(mPerPx) || mPerPx <= 0) return [];

    /*
     * v29 Fix:
     * +100 (1x1) = 10×10 Sensoren mit exakt 1,0 m Abstand.
     * +100 (1x2) nutzt dieselbe Logik wie das normale Raster
     * "1 Sensor / 2 m²": quadratischer Abstand sqrt(2) m in X und Y.
     * Der Klickpunkt ist bewusst der erste Sensor oben links, damit man
     * Blöcke sauber an bestehende Raster ausrichten kann.
     */
    const spacingM = mode === "block-1x2" ? Math.sqrt(2) : 1;
    const spacingPx = spacingM / mPerPx;
    const startX = anchor.x;
    const startY = anchor.y;
    const pts = [];

    for (let row = 0; row < ADD_BLOCK_ROWS; row += 1) {
      for (let col = 0; col < ADD_BLOCK_COLS; col += 1) {
        pts.push({
          x: startX + col * spacingPx - rasterOffset.x,
          y: startY + row * spacingPx - rasterOffset.y,
        });
      }
    }

    return pts;
  };

  const addSensorsAtPoint = (imgPoint) => {
    if (addPlacementMode === "block-1x1" || addPlacementMode === "block-1x2") {
      const newPts = createAddBlockPoints(imgPoint, addPlacementMode);
      if (!newPts.length) return;

      const firstNewIndex = addedPoints.length;
      const newIds = newPts.map((_, i) => `a-${firstNewIndex + i}`);

      setAddedPoints((pts) => [...pts, ...newPts]);
      setSelectedIds(newIds);
      setMoveSelectionMode(true);
      setAddMode(false);
      setAddPlacementMode("single");
      setHistory((h) => [
        ...h,
        { type: "add", count: newPts.length, idsAdded: newIds },
      ]);
      return;
    }

    const newPt = {
      x: imgPoint.x - rasterOffset.x,
      y: imgPoint.y - rasterOffset.y,
    };
    setAddedPoints((pts) => [...pts, newPt]);
    setHistory((h) => [...h, { type: "add", count: 1 }]);
  };

  /* ── Drehen starten (öffnet Steuerungs-Panel) ── */
  const handleRotateStart = () => {
    const coords = getSelectedSensorCoords();
    if (!coords.length) return;
    const center = getCenterOfPoints(coords);
    setRotateCenter(center);
    setRotateAngle(0); // Grad (nicht Rad)
    setRotateMode(true);
    setContextMenu(null);
  };

  /* ── Drehen anwenden ── */
  const applyRotation = () => {
    if (!rotateCenter || rotateAngle === 0) {
      setRotateMode(false);
      return;
    }

    const angleRad = (rotateAngle * Math.PI) / 180; // Grad → Rad

    const ptFor = (id) =>
      id.startsWith("s-")
        ? sensorPoints[+id.slice(2)]
        : addedPoints[+id.slice(2)];

    const prevOffsets = {};
    setSensorOffsets((prev) => {
      const nxt = { ...prev };
      selectedIds.forEach((id) => {
        const base = ptFor(id);
        if (!base) return;
        const off = prev[id] || { x: 0, y: 0 };
        const absX = base.x + rasterOffset.x + off.x;
        const absY = base.y + rasterOffset.y + off.y;
        const dx = absX - rotateCenter.x;
        const dy = absY - rotateCenter.y;
        const newAbsX =
          rotateCenter.x + dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
        const newAbsY =
          rotateCenter.y + dx * Math.sin(angleRad) + dy * Math.cos(angleRad);
        prevOffsets[id] = { ...(prev[id] || { x: 0, y: 0 }) };
        nxt[id] = {
          x: newAbsX - base.x - rasterOffset.x,
          y: newAbsY - base.y - rasterOffset.y,
        };
      });
      return nxt;
    });

    setHistory((h) => [
      ...h,
      {
        type: "rotate",
        ids: [...selectedIds],
        prevOffsets,
        center: { ...rotateCenter },
        angle: rotateAngle, // in Grad gespeichert
      },
    ]);
    setRotateMode(false);
    setRotateAngle(0);
  };

  /* ── Löschen (über Kontextmenü) ── */
  const handleDeleteSelected = () => {
    if (!selectedIds.length) return;
    setDeletedIds((old) => [...old, ...selectedIds]);
    setHistory((h) => [...h, { type: "delete", idsDeleted: [...selectedIds] }]);
    setSelectedIds([]);
    setContextMenu(null);
    setRotateMode(false);
    setRotateAngle(0);
    setRotateCenter(null);
  };

  /* „Jeden zweiten löschen“ (v42): Schachbrett-Ausdünnung der Auswahl.
     Reihen werden über die y-Positionen geclustert. In Reihe 1 fällt jeder
     2., 4., … Sensor, in Reihe 2 jeder 1., 3., … – Reihe 3 wieder wie
     Reihe 1 usw. Das halbiert die Dichte diagonal versetzt. */
  const handleDeleteEverySecond = () => {
    if (selectedSensorItems.length < 2) return;

    const items = selectedSensorItems.map((pt) => {
      const off = sensorOffsets[pt.id] || { x: 0, y: 0 };
      return { id: pt.id, x: pt.x + off.x, y: pt.y + off.y };
    });

    const sortedByY = [...items].sort((a, b) => a.y - b.y || a.x - b.x);

    /* Reihen-Toleranz: halber kleinster vertikaler Abstand */
    let minRowGap = Infinity;
    for (let i = 1; i < sortedByY.length; i += 1) {
      const gap = sortedByY[i].y - sortedByY[i - 1].y;
      if (gap > 2) minRowGap = Math.min(minRowGap, gap);
    }
    const rowTolerance = Number.isFinite(minRowGap) ? minRowGap / 2 : 10;

    const rows = [];
    sortedByY.forEach((item) => {
      const lastRow = rows[rows.length - 1];
      if (lastRow && Math.abs(item.y - lastRow.refY) <= rowTolerance) {
        lastRow.items.push(item);
      } else {
        rows.push({ refY: item.y, items: [item] });
      }
    });

    const idsToDelete = [];
    rows.forEach((row, rowIndex) => {
      row.items.sort((a, b) => a.x - b.x);
      row.items.forEach((item, colIndex) => {
        if ((rowIndex + colIndex) % 2 === 1) idsToDelete.push(item.id);
      });
    });

    if (!idsToDelete.length) return;
    const deleteSet = new Set(idsToDelete);
    setDeletedIds((old) => [...old, ...idsToDelete]);
    setHistory((h) => [...h, { type: "delete", idsDeleted: [...idsToDelete] }]);
    setSelectedIds((ids) => ids.filter((id) => !deleteSet.has(id)));
    setContextMenu(null);
  };

  /* Aura-Kontextmenü (v42/v45/v47) */
  const handleAuraDuplicate = (auraId) => {
    const source = auraPoints.find((a) => a.id === auraId);
    setContextMenu(null);
    if (!source) return;
    const offset = Math.min(300, mPerPx ? 3 / mPerPx : 60); // ~3 m Versatz
    const copy = {
      id: "aura-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8),
      x: source.x + offset,
      y: source.y + offset,
      color: getAuraColor(source),
    };
    setAuraPoints((pts) => [...pts, copy]);
    setHistory((h) => [...h, { type: "auraAdd", aura: copy }]);
  };

  const handleAuraColorChange = (auraId, color) => {
    const aura = auraPoints.find((a) => a.id === auraId);
    setContextMenu(null);
    if (!aura) return;
    const prevColor = aura.color || null;
    setAuraPoints((pts) =>
      pts.map((a) =>
        a.id === auraId ? { ...a, color: color || DEFAULT_AURA_COLOR } : a
      )
    );
    setHistory((h) => [...h, { type: "auraColor", id: auraId, prevColor }]);
  };

  const handleAuraDeleteFromMenu = (auraId) => {
    const aura = auraPoints.find((a) => a.id === auraId);
    setContextMenu(null);
    if (!aura) return;
    setAuraPoints((pts) => pts.filter((a) => a.id !== auraId));
    setHistory((h) => [...h, { type: "auraDelete", aura }]);
  };

  useEffect(() => {
    if (step !== 5) return;

    const onKeyDown = (evt) => {
      const target = evt.target;
      const tagName = target?.tagName?.toLowerCase?.();
      const isTypingTarget =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        target?.isContentEditable;

      if (isTypingTarget) return;

      const isCopyShortcut =
        (evt.metaKey || evt.ctrlKey) &&
        !evt.shiftKey &&
        !evt.altKey &&
        evt.key?.toLowerCase?.() === "c";

      if (isCopyShortcut && selectedIds.length > 0) {
        evt.preventDefault();
        copySelectionToClipboard({ activatePasteMode: false });
        return;
      }

      const isPasteShortcut =
        (evt.metaKey || evt.ctrlKey) &&
        !evt.shiftKey &&
        !evt.altKey &&
        evt.key?.toLowerCase?.() === "v";

      if (isPasteShortcut && clipboard && clipboard.length > 0) {
        evt.preventDefault();
        handleKeyboardPaste();
        return;
      }

      if (selectedIds.length === 0) return;

      if (evt.key === "Delete" || evt.key === "Backspace") {
        evt.preventDefault();
        handleDeleteSelected();
        return;
      }

      const arrowMove = {
        ArrowLeft: { x: -1, y: 0 },
        ArrowRight: { x: 1, y: 0 },
        ArrowUp: { x: 0, y: -1 },
        ArrowDown: { x: 0, y: 1 },
      }[evt.key];

      if (arrowMove && !evt.altKey && !evt.ctrlKey && !evt.metaKey && mPerPx) {
        evt.preventDefault();
        const stepM = evt.shiftKey ? 1 : 0.1;
        const stepPx = stepM / mPerPx;
        moveSelectedSensorsBy(arrowMove.x * stepPx, arrowMove.y * stepPx);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    step,
    selectedIds,
    mPerPx,
    clipboard,
    clipboardCenter,
    stageScale,
    imageObj,
    sensorPoints,
    addedPoints,
    sensorOffsets,
    sensorColors,
    rasterOffset,
  ]);

  /* ────────── Rechtsklick (Kontextmenü) ────────── */
  const handleContextMenu = (e) => {
    e.evt.preventDefault();

    /* v45: Rechtsklick auf den Aura-Mittelpunkt öffnet immer das
       Aura-Menü – auch außerhalb des Aura-Modus. */
    if (step === 5) {
      const targetName =
        e.target && typeof e.target.name === "function" ? e.target.name() : "";
      if (targetName && targetName.indexOf("aura-") === 0) {
        setContextMenu({
          type: "aura",
          auraId: targetName,
          screenX: e.evt.clientX,
          screenY: e.evt.clientY,
        });
        return;
      }
    }

    if (!moveSelectionMode) return;

    const stage = e.target.getStage();
    const ptr = stage.getPointerPosition();
    const imgPos = toImage(ptr);

    // Paste-Modus: Rechtsklick = Einfügen
    if (pasteMode && clipboard) {
      handlePaste(imgPos.x, imgPos.y);
      return;
    }

    // Auswahl vorhanden → Kontextmenü anzeigen
    if (selectedIds.length > 0) {
      setContextMenu({
        screenX: e.evt.clientX,
        screenY: e.evt.clientY,
        imgX: imgPos.x,
        imgY: imgPos.y,
      });
    }
  };

  /* ---------------- Clicks ---------------- */
  const handleCanvasClick = (e) => {
    if (e.evt.button !== 0 || e.evt.altKey) {
      return;
    }

    // Kontextmenü schließen bei Linksklick
    if (contextMenu) {
      setContextMenu(null);
      return;
    }

    // Im Dreh-Modus keine Klick-Aktionen
    if (rotateMode) return;

    /* Schritt 6: Klick neben einen Abdeckblock hebt die Auswahl auf.
       Klicks auf Blöcke/Griffe (name beginnt mit "cover") bleiben unberührt. */
    if (step === 6) {
      const clickTargetName =
        e.target && typeof e.target.name === "function" ? e.target.name() : "";
      /* Klicks auf Blöcke ODER auf den Transformer (Anker/Rahmen) lassen
         die Auswahl bestehen – sonst verschwinden die Anfasser beim
         Skalieren (v43-Fix). */
      let insideTransformer = false;
      let node = e.target;
      while (node) {
        if (
          typeof node.getClassName === "function" &&
          node.getClassName() === "Transformer"
        ) {
          insideTransformer = true;
          break;
        }
        node = typeof node.getParent === "function" ? node.getParent() : null;
      }
      if (
        !insideTransformer &&
        (!clickTargetName || clickTargetName.indexOf("cover") !== 0)
      ) {
        setSelectedCoverId(null);
      }
      return;
    }

    const p = toImage(e.target.getStage().getPointerPosition());

    /* ───── Schritt 5: Kopierte Auswahl per normalem Klick einfügen ───── */
    if (step === 5 && pasteMode && clipboard) {
      handlePaste(p.x, p.y);
      return;
    }

    /* ───── Schritt 5: Sensorpunkt datenbasiert löschen ─────
       v20: Sensoren werden gebündelt gerendert. Deshalb wird beim Klick
       der nächstgelegene Sensor aus dem Datenmodell gesucht. */
    if (step === 5 && deleteMode) {
      const deleteId = findNearestSensorId(p);
      if (deleteId) {
        setDeletedIds((ids) => [...ids, deleteId]);
        setHistory((h) => [...h, { type: "delete", idsDeleted: [deleteId] }]);
      }
      return;
    }

    /* ───── Schritt 5: Sensoren einzeln per Klick auswählen ─────
       Im Auswahl-Modus kann ein einzelner Sensor direkt markiert werden.
       Shift, Cmd (Apple) oder Ctrl fügt weitere Sensoren zur Auswahl hinzu. */
    if (
      step === 5 &&
      moveSelectionMode &&
      !areaDeleteMode &&
      !addMode &&
      !auraMode
    ) {
      const sensorId = findNearestSensorId(p);
      if (sensorId) {
        selectSensorByClick(sensorId, isAddToSelectionEvent(e.evt));
        return;
      }
    }

    /* Schritt 2 – Maßstabs­punkte */
    if (step === 2 && scalePoints.length < 4) {
      setScalePoints((prev) => [...prev, p.x, p.y]);
      return;
    }
    if (step === 3 && !polyClosed) {
      if (polygonPoints.length >= 4) {
        const [fx, fy] = polygonPoints;
        if (Math.hypot(p.x - fx, p.y - fy) < 12) {
          setPolyClosed(true);
          return;
        }
      }
      setPolygonPoints((prev) => [...prev, p.x, p.y]);
    }
    /* ───── Schritt 5: Sensorpunkt(e) hinzufügen ─────
       v28: Einzelne Sensoren und +100-Blöcke dürfen bewusst frei auf
       dem Plan gesetzt werden. So funktionieren manuelle Korrekturen
       auch an unruhigen Polygonrändern zuverlässig. */
    if (step === 5 && addMode) {
      addSensorsAtPoint(p);
      return;
    }

    /* ───── Schritt 5: Aura hinzufügen / löschen ───── */
    if (step === 5 && auraMode) {
      const targetName =
        e.target && typeof e.target.name === "function" ? e.target.name() : "";
      if (targetName && targetName.indexOf("aura-") === 0) {
        /* v42: Löschen und Duplizieren laufen jetzt über das
           Rechtsklick-Menü – ein Linksklick auf einen Aura tut nichts. */
        return;
      }
      const newAura = {
        id: "aura-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8),
        x: p.x - rasterOffset.x,
        y: p.y - rasterOffset.y,
        color: DEFAULT_AURA_COLOR,
      };
      setAuraPoints((pts) => [...pts, newAura]);
      setHistory((h) => [...h, { type: "auraAdd", aura: newAura }]);
      return;
    }
  };

  /* ---------------- Helpers -------------- */
  const applyScale = () => {
    if (scalePoints.length !== 4) return;
    const px = Math.hypot(
      scalePoints[0] - scalePoints[2],
      scalePoints[1] - scalePoints[3]
    );
    const real = parseFloat(realDistance);
    if (!px || !real) return;
    setMPerPx(real / px);
    setStep(3);
  };

  const undoLast = () => !polyClosed && setPolygonPoints((p) => p.slice(0, -2));
  const resetPoly = () => {
    setPolygonPoints([]);
    setPolyClosed(false);
  };

  const undoScalePoint = () => setScalePoints((pts) => pts.slice(0, -2));

  const pointInPoly = (x, y) => {
    let inside = false;
    for (
      let i = 0, j = polygonPoints.length - 2;
      i < polygonPoints.length;
      i += 2
    ) {
      const xi = polygonPoints[i],
        yi = polygonPoints[i + 1];
      const xj = polygonPoints[j],
        yj = polygonPoints[j + 1];
      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
      j = i;
    }
    return inside;
  };

  const polyAreaPx = () => {
    if (!polyClosed || polygonPoints.length < 6) return 0;
    let a = 0;
    for (let i = 0; i < polygonPoints.length; i += 2) {
      const x1 = polygonPoints[i],
        y1 = polygonPoints[i + 1];
      const x2 = polygonPoints[(i + 2) % polygonPoints.length],
        y2 = polygonPoints[(i + 3) % polygonPoints.length];
      a += x1 * y2 - x2 * y1;
    }
    return Math.abs(a / 2);
  };

  const finalizePoly = () => {
    const areaPx = polyAreaPx();
    if (!areaPx || !mPerPx) return;
    setAreaM2(areaPx * mPerPx * mPerPx);
    setStep(4);
  };

  async function pdfToPngDataUrl(file, options = {}) {
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const page = await pdf.getPage(1);

    const baseViewport = page.getViewport({ scale: 1 });
    let scale;

    if (options.special) {
      scale = Math.min(
        SPECIAL_PDF_TARGET_SCALE,
        SPECIAL_PDF_MAX_WIDTH / baseViewport.width,
        SPECIAL_PDF_MAX_HEIGHT / baseViewport.height
      );
      scale = Math.max(1, scale);

      const projectedPixels =
        baseViewport.width * scale * baseViewport.height * scale;
      if (projectedPixels > SPECIAL_PDF_MAX_PIXELS) {
        scale *= Math.sqrt(SPECIAL_PDF_MAX_PIXELS / projectedPixels);
      }
    } else {
      scale = Math.min(MAX_IMG_WIDTH / baseViewport.width, 1);
    }

    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));

    await page.render({
      canvasContext: canvas.getContext("2d"),
      viewport,
    }).promise;

    return options.special
      ? canvas.toDataURL("image/png")
      : canvas.toDataURL("image/jpeg", JPG_QUALITY);
  }

  /* ---------- Sensor Points ---------- */
  const generateGridPointsBySpacing = (spacingM, includePoint) => {
    const spacingPx = spacingM / mPerPx;
    const xs = polygonPoints.filter((_, i) => i % 2 === 0);
    const ys = polygonPoints.filter((_, i) => i % 2 === 1);
    const minX = Math.min(...xs),
      maxX = Math.max(...xs);
    const minY = Math.min(...ys),
      maxY = Math.max(...ys);
    const pts = [];

    if (!Number.isFinite(spacingPx) || spacingPx <= 0) return pts;

    for (let y = minY; y <= maxY; y += spacingPx) {
      for (let x = minX; x <= maxX; x += spacingPx) {
        if (pointInPoly(x, y) && includePoint(x, y)) pts.push({ x, y });
      }
    }
    return pts;
  };

  const generateSensorPoints = () => {
    const raster = SENSOR_RASTER_LABELS[String(sensorRaster)]
      ? String(sensorRaster)
      : "1";
    const spacingM = Math.sqrt(parseFloat(raster));
    return generateGridPointsBySpacing(spacingM, () => true);
  };

  useEffect(() => {
    if (step === 5 && polyClosed && mPerPx) {
      const pts = generateSensorPoints();
      setSensorPoints(pts);
      setSensorCount(pts.length);
    }
  }, [step, sensorRaster]);

  useEffect(() => {
    if (!imageObj || !containerRef.current) return;

    const cw = containerRef.current.clientWidth;
    const ch = Math.min(TARGET_IMG_MAX_H, window.innerHeight - 180);
    const scaleToFit = Math.min(cw / imageObj.width, ch / imageObj.height);

    setStageScale(scaleToFit);
    setStagePos({
      x: (cw - imageObj.width * scaleToFit) / 2,
      y: (ch - imageObj.height * scaleToFit) / 2,
    });
  }, [imageObj]);

  /* ---------------- PDF ---------------- */
  /* ────────── Schritt 6: Abdeckblöcke & Druckeinstellungen (v41) ────────── */

  const addCoverBlock = () => {
    if (!imageObj) return;

    const container = containerRef.current;
    const viewW = container ? container.clientWidth : imageObj.width;
    const viewH = container ? container.clientHeight : imageObj.height;
    const safeScale = Math.max(stageScale, 0.001);

    const width = Math.min(
      imageObj.width,
      Math.max(COVER_BLOCK_MIN_SIZE * 2, Math.round(imageObj.width * 0.16))
    );
    const height = Math.min(
      imageObj.height,
      Math.max(COVER_BLOCK_MIN_SIZE * 2, Math.round(imageObj.height * 0.1))
    );

    /* Neuer Block erscheint in der Mitte des aktuell sichtbaren Ausschnitts. */
    let centerX = (viewW / 2 - stagePos.x) / safeScale;
    let centerY = (viewH / 2 - stagePos.y) / safeScale;
    centerX = Math.min(
      Math.max(centerX, width / 2),
      imageObj.width - width / 2
    );
    centerY = Math.min(
      Math.max(centerY, height / 2),
      imageObj.height - height / 2
    );

    const block = {
      id: "cover-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7),
      x: Math.round(centerX - width / 2),
      y: Math.round(centerY - height / 2),
      width,
      height,
    };

    setCoverBlocks((blocks) => [...blocks, block]);
    setSelectedCoverId(block.id);
    setHistory((h) => [...h, { type: "coverAdd", block: { ...block } }]);
  };

  const removeCoverBlock = (id) => {
    const block = coverBlocks.find((b) => b.id === id);
    if (!block) return;
    setCoverBlocks((blocks) => blocks.filter((b) => b.id !== id));
    setSelectedCoverId((sel) => (sel === id ? null : sel));
    setHistory((h) => [...h, { type: "coverRemove", block: { ...block } }]);
  };

  const commitCoverBlockChange = (id, attrs, prevBlock) => {
    setCoverBlocks((blocks) =>
      blocks.map((b) => (b.id === id ? { ...b, ...attrs } : b))
    );
    if (prevBlock) {
      setHistory((h) => [
        ...h,
        { type: "coverChange", block: { ...prevBlock } },
      ]);
    }
  };

  /* v44: Verschieben und Skalieren der Abdeckblöcke laufen über eigene
     Eck-Anfasser. WÄHREND der Geste werden nur die Konva-Nodes direkt
     bewegt (kein setState – das verursachte früher das 1-Pixel-Ruckeln);
     erst am Ende der Geste wird der Zustand einmalig übernommen. */
  const syncCoverUi = (box) => {
    const ui = coverUiRefs.current;
    if (ui.outline) {
      ui.outline.setAttrs({
        x: box.x,
        y: box.y,
        width: box.width,
        height: box.height,
      });
    }
    const place = {
      nw: { x: box.x, y: box.y },
      ne: { x: box.x + box.width, y: box.y },
      sw: { x: box.x, y: box.y + box.height },
      se: { x: box.x + box.width, y: box.y + box.height },
    };
    Object.keys(place).forEach((key) => {
      const handle = ui.handles[key];
      if (handle && !handle.isDragging()) handle.position(place[key]);
    });
  };

  const applyCoverResize = (block, corner, handleNode) => {
    const start = coverGestureRef.current;
    if (!start) return;

    /* Ankerpunkt = gegenüberliegende Ecke der Box beim Gesten-Start */
    const anchor = {
      nw: { x: start.x + start.width, y: start.y + start.height },
      ne: { x: start.x, y: start.y + start.height },
      sw: { x: start.x + start.width, y: start.y },
      se: { x: start.x, y: start.y },
    }[corner];
    if (!anchor) return;

    const pos = handleNode.position();
    const width = Math.max(COVER_BLOCK_MIN_SIZE, Math.abs(pos.x - anchor.x));
    const height = Math.max(COVER_BLOCK_MIN_SIZE, Math.abs(pos.y - anchor.y));
    const box = {
      x: pos.x < anchor.x ? anchor.x - width : anchor.x,
      y: pos.y < anchor.y ? anchor.y - height : anchor.y,
      width,
      height,
    };

    const mainNode = coverNodeRefs.current[block.id];
    if (mainNode) mainNode.setAttrs(box);

    /* Gezogenen Griff auf die (ggf. geklemmte) Box-Ecke setzen */
    handleNode.position({
      x: pos.x < anchor.x ? box.x : box.x + box.width,
      y: pos.y < anchor.y ? box.y : box.y + box.height,
    });

    syncCoverUi(box);
    if (mainNode) mainNode.getLayer()?.batchDraw();
    handleNode.getLayer()?.batchDraw();
  };

  const finishCoverResize = (block) => {
    coverGestureRef.current = null;
    const mainNode = coverNodeRefs.current[block.id];
    if (!mainNode) return;
    commitCoverBlockChange(
      block.id,
      {
        x: Math.round(mainNode.x()),
        y: Math.round(mainNode.y()),
        width: Math.round(mainNode.width()),
        height: Math.round(mainNode.height()),
      },
      block
    );
  };

  /* Schwebendes Druck-Panel per Kopfzeile verschieben (v42) */
  const startPrintPanelDrag = (e) => {
    if (e.button !== 0) return;
    const layout = printLayoutRef.current;
    const panel = printPanelRef.current;
    if (!layout || !panel) return;
    e.preventDefault();

    const layoutRect = layout.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const grabX = e.clientX - panelRect.left;
    const grabY = e.clientY - panelRect.top;

    const onMove = (ev) => {
      const maxX = Math.max(0, layoutRect.width - panelRect.width);
      const maxY = Math.max(0, layoutRect.height - 60);
      setPrintPanelPos({
        x: Math.min(Math.max(ev.clientX - layoutRect.left - grabX, 0), maxX),
        y: Math.min(Math.max(ev.clientY - layoutRect.top - grabY, 0), maxY),
      });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  /* Sinnvolle Vorbelegung der Anzahl beim Aktivieren eines Produkts –
     jederzeit manuell änderbar. */
  const defaultPrintProductCount = (productId) => {
    if (productId === "wd1" || productId === "kd1") {
      return currentSensorCount > 0 ? String(currentSensorCount) : "";
    }
    if (productId === "auraSensor") {
      return auraPoints.length > 0 ? String(auraPoints.length) : "1";
    }
    return "1";
  };

  const setPrintProductCount = (productId, value) =>
    setPrintProductCounts((prev) => ({
      ...prev,
      [productId]: value.replace(/[^\d]/g, "").slice(0, 5),
    }));

  const setPrintField = (field, value) =>
    setPrintFields((prev) => ({ ...prev, [field]: value }));

  const togglePrintShow = (key) =>
    setPrintShow((prev) => ({ ...prev, [key]: !prev[key] }));

  const togglePrintProduct = (productId) => {
    const isEnabling = !printProducts.includes(productId);
    setPrintProducts((prev) => {
      const next = prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId];
      /* Reihenfolge im PDF folgt immer der Katalog-Reihenfolge. */
      return PDF_PRODUCT_CATALOG.filter((p) => next.includes(p.id)).map(
        (p) => p.id
      );
    });
    if (isEnabling) {
      setPrintProductCounts((prev) =>
        prev[productId] === undefined || prev[productId] === ""
          ? { ...prev, [productId]: defaultPrintProductCount(productId) }
          : prev
      );
    }
  };

  /* Angebots-Modal: öffnet das Mailprogramm mit vorbefüllter Preisanfrage. */
  const buildOfferMailto = () => {
    const productLabels = PDF_PRODUCT_CATALOG.filter((p) =>
      printProducts.includes(p.id)
    ).map((p) => p.fullLabel);

    const bodyLines = [
      "Guten Tag,",
      "",
      "über den HUM-ID Sensor-Planer habe ich eine Verlegeempfehlung erstellt und bitte um ein unverbindliches Angebot.",
      "",
      `Projekt: ${projectName || "Unbenanntes Projekt"}`,
      Number.isFinite(areaM2) ? `Sensorfläche: ${areaM2.toFixed(1)} m²` : null,
      `Anzahl HUM-ID Sensoren: ${currentSensorCount}`,
      AURA_FEATURE_ENABLED && auraPoints.length > 0
        ? `AURA Messpunkte: ${auraPoints.length}`
        : null,
      `Sensor-Raster: ${getSensorRasterExportLabel(sensorRaster)}`,
      productLabels.length > 0 ? `Produkte: ${productLabels.join(", ")}` : null,
      "",
      "Mit freundlichen Grüßen",
    ].filter((line) => line !== null);

    const subject = `Preisanfrage zu Projekt ${projectName || "Sensor-Plan"}`;
    return `mailto:info@hum-id.com?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
  };

  /* Tastatur in Schritt 6: Entf/Backspace löscht den ausgewählten
     Abdeckblock, Escape hebt die Auswahl auf. */
  useEffect(() => {
    if (step !== 6) return;

    const onKeyDown = (evt) => {
      const target = evt.target;
      const tagName = target?.tagName?.toLowerCase?.();
      const isTypingTarget =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        target?.isContentEditable;
      if (isTypingTarget) return;

      if (
        (evt.key === "Delete" || evt.key === "Backspace") &&
        selectedCoverId
      ) {
        evt.preventDefault();
        removeCoverBlock(selectedCoverId);
        return;
      }
      if (evt.key === "Escape") {
        setSelectedCoverId(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step, selectedCoverId, coverBlocks]);

  const capturePlanJpeg = async () => {
    const stage = stageRef.current;
    if (!stage) return null;

    const prevScale = stage.scaleX();
    const prevPos = { x: stage.x(), y: stage.y() };
    const uiLayer = uiLayerRef.current;

    /* Bearbeitungshilfen (Rahmen/Griffe der Abdeckblöcke) sind nie Teil
       des Exports – die weißen Blockflächen selbst dagegen schon. */
    if (uiLayer) uiLayer.hide();
    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.batchDraw();
    const pngDataUrl = stage.toDataURL({ pixelRatio: 1 });
    stage.scale({ x: prevScale, y: prevScale });
    stage.position(prevPos);
    if (uiLayer) uiLayer.show();
    stage.batchDraw();

    return new Promise((res) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.width;
        c.height = img.height;
        c.getContext("2d").drawImage(img, 0, 0);
        /* v41: 0.85 statt 0.7 – deutlich schärferes Druckbild bei
           moderat größerer Datei. */
        res(c.toDataURL("image/jpeg", PDF_PLAN_JPEG_QUALITY));
      };
      img.src = pngDataUrl;
    });
  };

  /* Externe PDF-Assets (Schriften, Logo, Produktbilder) werden pro Sitzung
     nur einmal geladen – Vorschau und Download teilen sich den Cache. */
  const loadPdfAsset = async (key, loader) => {
    const cache = pdfAssetCacheRef.current;
    if (!(key in cache)) {
      cache[key] = await loader();
    }
    return cache[key];
  };

  const buildPdfStatsLine = (useFields = false) => {
    if (useFields) {
      /* v45: Im gestalteten PDF steht in der Fußzeile nur, was der Nutzer
         in Schritt 6 selbst in die Kennzahlen-Felder schreibt. Keine
         automatisch ergänzten Sensor- oder Aura-Anzahlen. */
      return [printFields.statsArea, printFields.statsRaster]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join("  ·  ");
    }

    const defaultArea = `${areaM2?.toFixed(1) || "-"} m² Sensorfläche`;
    const defaultRaster = getSensorRasterExportLabel(sensorRaster);
    return [
      defaultArea,
      `${currentSensorCount} HUM-ID Sensoren`,
      ...(AURA_FEATURE_ENABLED ? [`${auraPoints.length} AURA`] : []),
      defaultRaster,
    ].join("  ·  ");
  };

  /* Erzeugt das komplette PDF-Dokument. Vorschau und Download nutzen exakt
     denselben Code-Pfad – die Vorschau ist damit immer verbindlich.
     withFrame=false: Schnell-Export (neues Grundlayout ohne Rahmenfelder).
     withFrame=true:  Rahmen-Layout aus Schritt 6 (Projektangaben,
                      Produktübersicht, Logo- und Kennzahlen-Optionen). */
  const createPdfDocument = async ({ withFrame = false } = {}) => {
    const jpegDataUrl = await capturePlanJpeg();
    if (!jpegDataUrl) return null;

    const doc = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "l",
    });

    /* Schrift robust laden und einbetten; Fallback-Kette wie bisher,
       zuletzt Helvetica. */
    const firaSansRegularBase64 = await loadPdfAsset("font-regular", () =>
      loadFirstBase64(FIRA_SANS_REGULAR_URLS)
    );
    const firaSansBoldBase64 = await loadPdfAsset("font-bold", () =>
      loadFirstBase64(FIRA_SANS_BOLD_URLS)
    );
    const pdfFontFamily = firaSansRegularBase64 ? "FiraSansPDF" : "Helvetica";

    if (firaSansRegularBase64) {
      doc.addFileToVFS("FiraSans-Regular.ttf", firaSansRegularBase64);
      doc.addFont("FiraSans-Regular.ttf", "FiraSansPDF", "normal");
    }
    if (firaSansBoldBase64) {
      doc.addFileToVFS("FiraSans-Bold.ttf", firaSansBoldBase64);
      doc.addFont("FiraSans-Bold.ttf", "FiraSansPDF", "bold");
    }

    const setFontStyle = (style) =>
      doc.setFont(
        pdfFontFamily,
        style === "bold" && !firaSansBoldBase64 ? "normal" : style
      );
    setFontStyle("normal");

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const marginX = 10;
    const contentW = pageW - marginX * 2;

    /* HUM-ID Signatur: schmale Dreifarblinie (Blau/Grün/Orange). */
    const drawBrandLine = (y, h) => {
      /* v43: dezente graue Trennlinie statt der drei Markenfarben. */
      doc.setDrawColor(208, 213, 221);
      doc.setLineWidth(0.3);
      doc.line(marginX, y + h / 2, pageW - marginX, y + h / 2);
    };

    /* Logo oben rechts auf dunkler HUM-ID Fläche (weißes Kreislogo). */
    const drawLogoBox = async (boxX, boxY, boxW, boxH) => {
      const pdfLogoDataUrl = await loadPdfAsset("logo", () =>
        loadFirstDataUrl(PDF_LOGO_URLS)
      );
      if (!pdfLogoDataUrl) return;
      try {
        const pad = 3;
        doc.setFillColor(...HUMID_BRAND_RGB.ink);
        if (typeof doc.roundedRect === "function") {
          doc.roundedRect(boxX, boxY, boxW, boxH, 3, 3, "F");
        } else {
          doc.rect(boxX, boxY, boxW, boxH, "F");
        }
        const props = doc.getImageProperties(pdfLogoDataUrl);
        const maxLogoW = boxW - pad * 2;
        const maxLogoH = boxH - pad * 2;
        const ratio = props.width / props.height;
        let logoW = maxLogoW;
        let logoH = logoW / ratio;
        if (logoH > maxLogoH) {
          logoH = maxLogoH;
          logoW = logoH * ratio;
        }
        doc.addImage(
          pdfLogoDataUrl,
          "PNG",
          boxX + (boxW - logoW) / 2,
          boxY + (boxH - logoH) / 2,
          logoW,
          logoH
        );
      } catch (error) {
        console.warn("PDF-Logo konnte nicht eingefügt werden:", error);
      }
    };

    /* Fußzeilen-Logo (v44): das normale farbige HUM-ID-Logo, ohne
       Hintergrundfläche – sitzt direkt auf dem weißen Papier. */
    const drawFooterLogo = async (boxX, boxY, boxW, boxH) => {
      const footerLogoDataUrl = await loadPdfAsset("footerLogo", () =>
        loadFirstDataUrl(PDF_FOOTER_LOGO_URLS)
      );
      if (!footerLogoDataUrl) return;
      try {
        const props = doc.getImageProperties(footerLogoDataUrl);
        const ratio = props.width / props.height;
        let logoW = boxW;
        let logoH = logoW / ratio;
        if (logoH > boxH) {
          logoH = boxH;
          logoW = logoH * ratio;
        }
        doc.addImage(
          footerLogoDataUrl,
          "JPEG",
          boxX + (boxW - logoW),
          boxY + (boxH - logoH) / 2,
          logoW,
          logoH
        );
      } catch (error) {
        console.warn("Fußzeilen-Logo konnte nicht eingefügt werden:", error);
      }
    };

    const statsLine = buildPdfStatsLine(withFrame);
    let cursorY = 10;

    if (withFrame && printShow.header) {
      /* Rahmen-Kopf: Überschrift, Untertitel, Meta-Zeile.
         Leere Felder werden schlicht weggelassen. */
      const title = (printFields.title || "").trim() || "HUM-ID Sensor-Plan";
      const subtitle = (printFields.subtitle || "").trim();
      const author = (printFields.author || "").trim();
      const dateValue = (printFields.date || "").trim();
      const versionValue = (printFields.version || "").trim();

      const metaParts = [];
      if (author) metaParts.push(`Erstellt von ${author}`);
      if (dateValue) metaParts.push(dateValue);
      if (versionValue) metaParts.push(`Version ${versionValue}`);

      doc.setTextColor(...HUMID_BRAND_RGB.ink);
      doc.setFontSize(17);
      setFontStyle("bold");
      doc.text(title, marginX, cursorY + 6);
      cursorY += 9;

      if (subtitle) {
        doc.setFontSize(10.5);
        setFontStyle("normal");
        doc.setTextColor(96, 104, 116);
        doc.text(subtitle, marginX, cursorY + 4);
        cursorY += 6;
      }
      if (metaParts.length) {
        doc.setFontSize(8.5);
        setFontStyle("normal");
        doc.setTextColor(142, 148, 158);
        doc.text(metaParts.join("  ·  "), marginX, cursorY + 3.5);
        cursorY += 5.5;
      }
    } else if (!withFrame) {
      /* Basis-Kopf (Schnell-Export): Titel + Kennzahlen, Logo oben rechts. */
      doc.setTextColor(...HUMID_BRAND_RGB.ink);
      doc.setFontSize(15);
      setFontStyle("bold");
      doc.text("HUM-ID Sensor-Plan", marginX, 16);
      doc.setFontSize(9);
      setFontStyle("normal");
      doc.setTextColor(96, 104, 116);
      doc.text(statsLine, marginX, 21.5);
      cursorY = 23;
    }

    /* Produktübersicht oben rechts im Kopf (nur Rahmen-Layout):
       Bildkachel mit Anzahl-Badge und Bezeichnung darunter – nimmt dem
       Plan keine eigene Zeile mehr weg. */
    const selectedProducts =
      withFrame && printShow.products
        ? PDF_PRODUCT_CATALOG.filter((p) => printProducts.includes(p.id))
        : [];

    if (selectedProducts.length > 0) {
      const gap = 4;
      const n = selectedProducts.length;
      const maxTilesW = 150;
      const tileW = Math.min(26, Math.max(15, (maxTilesW - gap * (n - 1)) / n));
      const imgSize = Math.min(16, tileW - 1);
      const tilesTop = 8;
      let tx = pageW - marginX - (n * tileW + gap * (n - 1));
      let tilesBottom = tilesTop + imgSize + 3.2;

      for (let i = 0; i < n; i += 1) {
        const product = selectedProducts[i];
        let productImage = null;
        try {
          productImage = await loadPdfAsset(`product-${product.id}`, () =>
            loadFirstDataUrl([product.url])
          );
        } catch (error) {
          console.warn(
            "Produktbild konnte nicht geladen werden:",
            product.id,
            error
          );
        }

        const boxX = tx + (tileW - imgSize) / 2;

        doc.setDrawColor(224, 228, 234);
        doc.setLineWidth(0.25);
        doc.setFillColor(248, 249, 251);
        if (typeof doc.roundedRect === "function") {
          doc.roundedRect(boxX, tilesTop, imgSize, imgSize, 1.4, 1.4, "FD");
        } else {
          doc.rect(boxX, tilesTop, imgSize, imgSize, "FD");
        }
        if (productImage) {
          try {
            doc.addImage(
              productImage,
              "JPEG",
              boxX + 0.8,
              tilesTop + 0.8,
              imgSize - 1.6,
              imgSize - 1.6
            );
          } catch (error) {
            console.warn(
              "Produktbild konnte nicht eingefügt werden:",
              product.id,
              error
            );
          }
        }

        /* Anzahl-Badge oben rechts auf der Bildkachel */
        const countValue = String(printProductCounts[product.id] ?? "").trim();
        if (countValue) {
          const badgeR = 3.1;
          const badgeX = boxX + imgSize - 1;
          const badgeY = tilesTop + 1;
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(120, 128, 138);
          doc.setLineWidth(0.3);
          doc.circle(badgeX, badgeY, badgeR, "FD");
          doc.setFontSize(countValue.length > 3 ? 5.2 : 6.6);
          setFontStyle("bold");
          doc.setTextColor(...HUMID_BRAND_RGB.ink);
          doc.text(countValue, badgeX, badgeY + 0.9, { align: "center" });
        }

        /* Bezeichnung unter der Kachel */
        doc.setFontSize(6.5);
        setFontStyle("normal");
        doc.setTextColor(96, 104, 116);
        const labelLines = doc
          .splitTextToSize(product.label, tileW + 2)
          .slice(0, 2);
        doc.text(labelLines, tx + tileW / 2, tilesTop + imgSize + 3.2, {
          align: "center",
        });
        tilesBottom = Math.max(
          tilesBottom,
          tilesTop + imgSize + 3.2 + (labelLines.length - 1) * 2.4
        );

        tx += tileW + gap;
      }

      cursorY = Math.max(cursorY, tilesBottom + 1.5);
    }

    /* Logo: Basis-Layout oben rechts wie gehabt; im Rahmen-Layout wandert
       es klein in die Fußzeile (Platz im Kopf gehört den Produkten). */
    if (!withFrame) {
      await drawLogoBox(pageW - 36 - marginX, 7, 36, 18);
      cursorY = Math.max(cursorY, 26.5);
    }

    drawBrandLine(cursorY + 1.5, 1.1);
    cursorY += 5.5;

    /* Fußzeile: Dreifarb-Akzent, optional Kennzahlen, Kontakt + Quelle. */
    const showStatsInFooter =
      withFrame && printShow.stats && statsLine.trim().length > 0;
    const footerAccentY = showStatsInFooter ? pageH - 16.5 : pageH - 11.5;
    drawBrandLine(footerAccentY, 0.7);

    doc.setFontSize(8);
    setFontStyle("normal");
    if (showStatsInFooter) {
      doc.setTextColor(96, 104, 116);
      doc.text(statsLine, marginX, pageH - 12);
    }
    let footerRightEdge = pageW - marginX;
    if (withFrame && printShow.logo) {
      await drawFooterLogo(pageW - marginX - 22, pageH - 12, 22, 8.5);
      footerRightEdge = pageW - marginX - 24.5;
    }

    doc.setFontSize(8);
    setFontStyle("normal");
    doc.setTextColor(130, 136, 146);
    doc.text(
      "HUM-ID GmbH · Chausseestraße 107 · 10115 Berlin · info@hum-id.com",
      marginX,
      pageH - 7
    );
    const rightFooterText = `hum-id.com/sensorplaner · ${formatTimestampDe()}`;
    const rightFooterWidth =
      (doc.getStringUnitWidth(rightFooterText) * 8) / doc.internal.scaleFactor;
    doc.text(rightFooterText, footerRightEdge - rightFooterWidth, pageH - 7);

    /* Plan zentriert in die verbleibende Fläche einpassen. Jedes
       deaktivierte Element gibt seinen Platz an den Plan zurück. */
    const planImg = new Image();
    planImg.src = jpegDataUrl;
    await planImg.decode();

    const planTop = cursorY + 1;
    const planBottom = footerAccentY - 2.5;
    const availH = Math.max(planBottom - planTop, 20);
    const availW = contentW;

    let drawH = availH;
    let drawW = (planImg.width / planImg.height) * drawH;
    if (drawW > availW) {
      drawW = availW;
      drawH = (planImg.height / planImg.width) * drawW;
    }
    const planX = marginX + (availW - drawW) / 2;
    const planY = planTop + (availH - drawH) / 2;
    doc.addImage(
      jpegDataUrl,
      "JPEG",
      planX,
      planY,
      drawW,
      drawH,
      undefined,
      "FAST"
    );

    return doc;
  };

  const handleDownload = async ({ withFrame = false } = {}) => {
    if (isPdfBusy) return;
    setIsPdfBusy(true);
    try {
      const doc = await createPdfDocument({ withFrame });
      if (!doc) return;
      doc.save(safePdfFileName(projectName || fileName || "humid-sensorplan"));
      setShowOfferModal(true);
    } catch (error) {
      console.error(error);
      setProjectNotice(
        "PDF konnte nicht erstellt werden. Bitte erneut versuchen."
      );
    } finally {
      setIsPdfBusy(false);
    }
  };

  const handlePdfPreview = async () => {
    if (isPdfBusy) return;
    setIsPdfBusy(true);
    try {
      const doc = await createPdfDocument({ withFrame: true });
      if (!doc) return;
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(String(doc.output("bloburl")));
    } catch (error) {
      console.error(error);
      setProjectNotice(
        "PDF-Vorschau konnte nicht erstellt werden. Bitte erneut versuchen."
      );
    } finally {
      setIsPdfBusy(false);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
  };

  const dxfNum = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0";
    return n.toFixed(4).replace(/\.?0+$/, "");
  };

  const dxfText = (value) =>
    String(value ?? "")
      .replace(/[\r\n]+/g, " ")
      .replace(/[^\x20-\x7EÄÖÜäöüß€–—×²³]/g, "")
      .slice(0, 240);

  const handleDownloadDxf = () => {
    if (!mPerPx || !Number.isFinite(mPerPx) || mPerPx <= 0) {
      setProjectNotice(
        "DXF-Export nicht möglich: Bitte zuerst den Maßstab festlegen."
      );
      return;
    }

    const pair = (code, value) => `${code}\n${value}\n`;
    const toCadPoint = (x, y) => ({
      x: x * mPerPx,
      y: -y * mPerPx,
    });

    const addLayer = (name, color = 7) =>
      [
        pair(0, "LAYER"),
        pair(2, name),
        pair(70, 0),
        pair(62, color),
        pair(6, "CONTINUOUS"),
      ].join("");

    const addCircle = (layer, x, y, radius, color = null) => {
      const p = toCadPoint(x, y);
      return [
        pair(0, "CIRCLE"),
        pair(8, layer),
        color !== null ? pair(62, color) : "",
        pair(10, dxfNum(p.x)),
        pair(20, dxfNum(p.y)),
        pair(30, 0),
        pair(40, dxfNum(radius)),
      ].join("");
    };

    const addText = (layer, x, y, text, height = DXF_TEXT_HEIGHT_M) => {
      const p = toCadPoint(x, y);
      return [
        pair(0, "TEXT"),
        pair(8, layer),
        pair(10, dxfNum(p.x)),
        pair(20, dxfNum(p.y)),
        pair(30, 0),
        pair(40, dxfNum(height)),
        pair(1, dxfText(text)),
      ].join("");
    };

    const addPolyline = (layer, points, closed = true, color = null) => {
      if (!points || points.length < 4) return "";
      const header = [
        pair(0, "POLYLINE"),
        pair(8, layer),
        color !== null ? pair(62, color) : "",
        pair(66, 1),
        pair(70, closed ? 1 : 0),
      ].join("");
      let vertices = "";
      for (let i = 0; i < points.length; i += 2) {
        const p = toCadPoint(points[i], points[i + 1]);
        vertices += [
          pair(0, "VERTEX"),
          pair(8, layer),
          pair(10, dxfNum(p.x)),
          pair(20, dxfNum(p.y)),
          pair(30, 0),
        ].join("");
      }
      return `${header}${vertices}${pair(0, "SEQEND")}`;
    };

    const sensorItemsForDxf = activeSensorItems
      .map((pt) => getSensorAbsolutePointById(pt.id))
      .filter(Boolean);

    const layerForSensorColor = (color) => {
      const normalized = String(color || "").toUpperCase();
      if (normalized === SENSOR_COLORS.orange.toUpperCase()) {
        return { layer: "HUM-ID_SENSOR_ORANGE", color: 30 };
      }
      if (normalized === SENSOR_COLORS.blue.toUpperCase()) {
        return { layer: "HUM-ID_SENSOR_BLAU", color: 5 };
      }
      return { layer: "HUM-ID_SENSOR_GRUEN", color: 3 };
    };

    const dxfColorForAuraColor = (color) => {
      const normalized = String(color || DEFAULT_AURA_COLOR).toUpperCase();
      if (normalized === AURA_COLORS.green.toUpperCase()) return 3;
      if (normalized === AURA_COLORS.orange.toUpperCase()) return 30;
      if (normalized === AURA_COLORS.dark.toUpperCase()) return 8;
      return 5;
    };

    let dxf = "";
    dxf += pair(0, "SECTION") + pair(2, "HEADER") + pair(0, "ENDSEC");
    dxf += pair(0, "SECTION") + pair(2, "TABLES");
    dxf += pair(0, "TABLE") + pair(2, "LAYER") + pair(70, 7);
    dxf += addLayer("HUM-ID_INFO", 7);
    dxf += addLayer("HUM-ID_SENSOR_GRUEN", 3);
    dxf += addLayer("HUM-ID_SENSOR_ORANGE", 30);
    dxf += addLayer("HUM-ID_SENSOR_BLAU", 5);
    dxf += addLayer("HUM-ID_AURA", 5);
    dxf += addLayer("HUM-ID_AURA_RADIUS", 5);
    dxf += addLayer("HUM-ID_SENSORFLAECHE", 4);
    dxf += pair(0, "ENDTAB") + pair(0, "ENDSEC");
    dxf += pair(0, "SECTION") + pair(2, "ENTITIES");

    dxf += addText(
      "HUM-ID_INFO",
      0,
      -5 / mPerPx,
      `HUM-ID Sensor-Plan | Sensoren: ${sensorItemsForDxf.length} | Auras: ${
        auraPoints.length
      } | Raster: ${getSensorRasterExportLabel(sensorRaster)}`
    );

    if (polyClosed && polygonPoints.length >= 6) {
      dxf += addPolyline("HUM-ID_SENSORFLAECHE", polygonPoints, true, 4);
    }

    sensorItemsForDxf.forEach((pt) => {
      const { layer, color } = layerForSensorColor(pt.color);
      dxf += addCircle(layer, pt.x, pt.y, DXF_SENSOR_RADIUS_M, color);
    });

    if (AURA_FEATURE_ENABLED) {
      auraPoints.forEach((a) => {
        const absX = a.x + rasterOffset.x;
        const absY = a.y + rasterOffset.y;
        const auraColor = dxfColorForAuraColor(getAuraColor(a));
        dxf += addCircle("HUM-ID_AURA_RADIUS", absX, absY, 10, auraColor);
        dxf += addCircle("HUM-ID_AURA", absX, absY, 0.25, auraColor);
      });
    }

    dxf += pair(0, "ENDSEC") + pair(0, "EOF");

    const suggestedName = projectName || fileName || "humid-sensorplan";
    downloadTextFile(safeDxfFileName(suggestedName), dxf, "application/dxf");
    setProjectNotice("DXF-Datei wurde heruntergeladen.");
  };

  const toggleRasterDrag = () => setRasterDraggable((d) => !d);
  const toggleDeleteMode = () => setDeleteMode((d) => !d);
  const toggleAddMode = () => {
    setAddMode((m) => {
      const next = !m;
      if (!next) setAddPlacementMode("single");
      return next;
    });
  };

  const exitSelectionModeFromBackground = () => {
    if (step !== 5 || !moveSelectionMode) return;
    setMoveSelectionMode(false);
    setSelectedIds([]);
    setSelRect(null);
    setContextMenu(null);
    setPasteMode(false);
    setRotateMode(false);
    setRotateAngle(0);
    setRotateCenter(null);
  };

  const handleSelectionBackgroundMouseDown = (e) => {
    // Nur echte Klicks auf die freie Fläche schließen den Auswahlmodus.
    // Klicks/Drags auf der Konva-Stage, Sensoren oder UI-Elementen bubbled zwar
    // hoch, haben aber ein anderes target und bleiben deshalb unangetastet.
    if (e.button !== 0) return;
    if (e.target !== e.currentTarget) return;
    exitSelectionModeFromBackground();
  };

  const resetAppStates = () => {
    setStep(1);
    setFileName("");
    setProjectName("Unbenanntes Projekt");
    setBackgroundDataUrl(null);
    setImageObj(null);
    setProjectNotice("");

    setMPerPx(null);
    setRealDistance("");
    setScalePoints([]);

    setPolygonPoints([]);
    setPolyClosed(false);
    setAreaM2(null);

    setSensorRaster("1");

    setSensorCount(null);
    setSensorPoints([]);
    setSensorVisualScale(1);
    setAreaOverlayMode(DEFAULT_AREA_OVERLAY_MODE);
    setAreaOverlayTransparency(DEFAULT_AREA_OVERLAY_TRANSPARENCY);
    setRasterOffset({ x: 0, y: 0 });
    setRasterDraggable(false);
    setDeleteMode(false);
    setDeletedIds([]);
    setAddMode(false);
    setAddPlacementMode("single");
    setAddedPoints([]);
    setAreaDeleteMode(false);
    setSelRect(null);

    setMoveSelectionMode(false);
    setSelectedIds([]);
    setSensorOffsets({});
    setSensorColors({});

    setAuraMode(false);
    setAuraPoints([]);
    setAuraVisualScale(1);
    setAuraNumbersEnabled(false);

    setPrintFields(DEFAULT_PRINT_FIELDS());
    setPrintShow(DEFAULT_PRINT_SHOW());
    setPrintProducts([...DEFAULT_PRINT_PRODUCTS]);
    setPrintProductCounts({});
    setPrintPanelPos(null);
    setCoverBlocks([]);
    setSelectedCoverId(null);
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
    setIsPdfBusy(false);

    // Neue States zurücksetzen
    setContextMenu(null);
    setClipboard(null);
    setPasteMode(false);
    setRotateMode(false);
    setRotateAngle(0);
    setRotateCenter(null);
    setShowStep5Info(false);
    setIsFileLoading(false);

    setHistory([]);

    setStageScale(1);
    setStagePos({ x: 0, y: 0 });

    document
      .querySelectorAll('input[type="file"].file-input')
      .forEach((input) => {
        input.value = "";
      });
  };

  /* -------------- Components ------------ */
  /* Btn ist seit v41 auf Modulebene definiert (siehe oben). */

  // ───────── Mehrfach‑Lösch‑Rechteck Handler ─────────────────
  const handleRectStart = (e) => {
    if (!areaDeleteMode && !moveSelectionMode) return;
    // Rechte Maustaste ist für das Kontextmenü reserviert.
    // Ohne diese Abfrage kann ein Rechtsklick die Auswahl versehentlich löschen.
    if (e.evt && e.evt.button !== 0) return;
    // Im Dreh-Modus kein neues Rechteck starten
    if (rotateMode) return;

    const { x, y } = toImage(e.target.getStage().getPointerPosition());

    if (moveSelectionMode) {
      const clickedName =
        e.target && typeof e.target.name === "function" ? e.target.name() : "";
      if (clickedName && clickedName.indexOf("selected-sensor") !== -1) {
        return;
      }

      // Shift/Cmd/Ctrl + Klick ist für additive Einzel-Auswahl reserviert.
      if (isAddToSelectionEvent(e.evt)) {
        return;
      }

      // Wenn der Klick direkt auf/nahe einem Sensor startet, behandeln wir das
      // als Einzel-Auswahl und starten kein Rechteck, damit die Auswahl nicht
      // kurz vorher geleert wird.
      if (findNearestSensorId({ x, y })) {
        return;
      }

      setSelectedIds([]);
    }

    setSelRect({ x, y, w: 0, h: 0 });
  };

  const handleRectMove = (e) => {
    if (rotateMode) return;
    if ((!areaDeleteMode && !moveSelectionMode) || !selRect) return;
    const { x, y } = toImage(e.target.getStage().getPointerPosition());
    setSelRect((r) => ({ ...r, w: x - r.x, h: y - r.y }));
  };

  const handleRectEnd = () => {
    if ((!areaDeleteMode && !moveSelectionMode) || !selRect) return;

    const x1 = Math.min(selRect.x, selRect.x + selRect.w);
    const x2 = Math.max(selRect.x, selRect.x + selRect.w);
    const y1 = Math.min(selRect.y, selRect.y + selRect.h);
    const y2 = Math.max(selRect.y, selRect.y + selRect.h);

    const isInside = (pt, id) => {
      const off = sensorOffsets[id] || { x: 0, y: 0 };
      const px = pt.x + rasterOffset.x + off.x;
      const py = pt.y + rasterOffset.y + off.y;
      return px >= x1 && px <= x2 && py >= y1 && py <= y2;
    };

    const hits = activeSensorItems
      .filter((pt) => isInside(pt, pt.id))
      .map((pt) => pt.id);

    if (areaDeleteMode) {
      if (hits.length > 0) {
        setDeletedIds((old) => [...old, ...hits]);
        setHistory((h) => [...h, { type: "delete", idsDeleted: hits }]);
      }
    } else if (moveSelectionMode) {
      setSelectedIds(hits);
    }
    setSelRect(null);
  };

  /* ─── Berechne gedrehte Positionen für die Vorschau während des Drehens ─── */
  const getRotatedOffset = (id) => {
    if (!rotateMode || !rotateCenter || rotateAngle === 0) return null;
    if (!selectedIds.includes(id)) return null;

    const angleRad = (rotateAngle * Math.PI) / 180;

    const ptFor = (pid) =>
      pid.startsWith("s-")
        ? sensorPoints[+pid.slice(2)]
        : addedPoints[+pid.slice(2)];
    const base = ptFor(id);
    if (!base) return null;
    const off = sensorOffsets[id] || { x: 0, y: 0 };
    const absX = base.x + rasterOffset.x + off.x;
    const absY = base.y + rasterOffset.y + off.y;
    const dx = absX - rotateCenter.x;
    const dy = absY - rotateCenter.y;
    const newAbsX =
      rotateCenter.x + dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
    const newAbsY =
      rotateCenter.y + dx * Math.sin(angleRad) + dy * Math.cos(angleRad);
    return {
      x: newAbsX - rasterOffset.x - base.x,
      y: newAbsY - rasterOffset.y - base.y,
    };
  };

  const getSelectedSensorBounds = () => {
    if (!selectedSensorItems.length) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    selectedSensorItems.forEach((pt) => {
      const rotOff = getRotatedOffset(pt.id);
      const off = rotOff || sensorOffsets[pt.id] || { x: 0, y: 0 };
      const x = pt.x + off.x;
      const y = pt.y + off.y;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });

    if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;

    const safeScale = Math.max(stageScale, 0.001);
    const padding = Math.max(
      sensorOuterRadius + 6,
      SELECTION_BOX_PADDING_SCREEN / safeScale
    );

    return {
      x: minX - padding,
      y: minY - padding,
      width: Math.max(1, maxX - minX + padding * 2),
      height: Math.max(1, maxY - minY + padding * 2),
    };
  };

  const renderSelectionBox = () => {
    if (!moveSelectionMode || selectedSensorItems.length === 0) return null;
    const bounds = getSelectedSensorBounds();
    if (!bounds) return null;

    const safeScale = Math.max(stageScale, 0.001);
    const strokeWidth = Math.max(1.2, SELECTION_BOX_STROKE_SCREEN / safeScale);
    const handleSize = Math.max(6, SELECTION_BOX_HANDLE_SCREEN / safeScale);
    const dash = SELECTION_BOX_DASH_SCREEN.map((v) =>
      Math.max(2, v / safeScale)
    );
    const handleHalf = handleSize / 2;
    const handlePositions = [
      [bounds.x, bounds.y],
      [bounds.x + bounds.width, bounds.y],
      [bounds.x + bounds.width, bounds.y + bounds.height],
      [bounds.x, bounds.y + bounds.height],
    ];

    const selectionCountLabel = `${selectedSensorItems.length} ${
      selectedSensorItems.length === 1 ? "Sensor" : "Sensoren"
    }`;
    const badgeFontSize = 13 / safeScale;
    const badgePaddingX = 8 / safeScale;
    const badgePaddingY = 5 / safeScale;
    const badgeGap = 8 / safeScale;
    const badgeWidth =
      selectionCountLabel.length * (badgeFontSize * 0.58) + badgePaddingX * 2;
    const badgeHeight = badgeFontSize + badgePaddingY * 2;
    const badgeX = bounds.x + bounds.width - badgeWidth;
    const badgeY = Math.max(0, bounds.y - badgeHeight - badgeGap);

    return (
      <Group name="selected-sensor selection-bounds" listening>
        <Rect
          x={bounds.x}
          y={bounds.y}
          width={bounds.width}
          height={bounds.height}
          stroke="#ffffff"
          strokeWidth={strokeWidth * 2.6}
          dash={dash}
          opacity={0.92}
          name="selected-sensor selection-bounds-outline"
          cursor="move"
        />
        <Rect
          ref={selectionMarqueeRef}
          x={bounds.x}
          y={bounds.y}
          width={bounds.width}
          height={bounds.height}
          stroke={SELECTION_BOX_COLOR}
          strokeWidth={strokeWidth}
          dash={dash}
          name="selected-sensor selection-bounds-outline"
          cursor="move"
        />
        {handlePositions.map(([x, y], idx) => (
          <Rect
            key={idx}
            x={x - handleHalf}
            y={y - handleHalf}
            width={handleSize}
            height={handleSize}
            fill="#ffffff"
            stroke={SELECTION_BOX_COLOR}
            strokeWidth={Math.max(1, strokeWidth * 0.8)}
            name="selected-sensor selection-bounds-handle"
            cursor="move"
          />
        ))}

        <Group
          x={badgeX}
          y={badgeY}
          name="selected-sensor selection-count-badge"
          cursor="move"
        >
          <Rect
            width={badgeWidth}
            height={badgeHeight}
            fill="rgba(0,0,0,0.82)"
            cornerRadius={6 / safeScale}
            shadowColor="black"
            shadowBlur={4 / safeScale}
            shadowOpacity={0.24}
            listening={false}
          />
          <Text
            text={selectionCountLabel}
            x={badgePaddingX}
            y={badgePaddingY - 0.5 / safeScale}
            fontFamily="Fira Sans, Arial, sans-serif"
            fontSize={badgeFontSize}
            fontStyle="600"
            fill="#ffffff"
            listening={false}
          />
        </Group>
      </Group>
    );
  };

  const ZoomStage = ({ showSensors = false }) => {
    if (!imageObj) return <p>Bild wird geladen…</p>;
    return (
      <Stage
        width={imageObj.width}
        height={imageObj.height}
        x={stagePos.x}
        y={stagePos.y}
        scaleX={stageScale}
        scaleY={stageScale}
        draggable
        dragBoundFunc={restrictDragBounds}
        onMouseDown={handleRectStart}
        onMouseMove={handleRectMove}
        onMouseUp={handleRectEnd}
        onWheel={handleWheel}
        onClick={handleCanvasClick}
        onContextMenu={handleContextMenu}
        onDragEnd={(e) => {
          if (e.target === stageRef.current) {
            const st = stageRef.current;
            setStagePos({ x: st.x(), y: st.y() });
          }
        }}
        ref={stageRef}
      >
        <Layer>
          <KonvaImage image={imageObj} />

          {/* v21 UX: Im Auswahl-Modus wird nur die Planunterlage abgedunkelt.
              Polygon, Sensorpunkte und AURAs bleiben darüber klar sichtbar. */}
          {step === 5 && moveSelectionMode && (
            <Rect
              x={0}
              y={0}
              width={imageObj.width}
              height={imageObj.height}
              fill="rgba(0,0,0,0.38)"
              listening={false}
            />
          )}

          {/* Measure */}
          {step === 2 && scalePoints.length >= 2 && (
            <Circle
              x={scalePoints[0]}
              y={scalePoints[1]}
              radius={4}
              fill="#FF6363"
            />
          )}
          {step === 2 && scalePoints.length === 4 && (
            <>
              <Circle
                x={scalePoints[2]}
                y={scalePoints[3]}
                radius={4}
                fill="#FF6363"
              />
              <Line points={scalePoints} stroke="#00A3FF" strokeWidth={2} />
            </>
          )}

          {/* Polygon / Sensorflächen-Hintergrund */}
          {step >= 3 &&
            (() => {
              const isPolygonEditStep = step === 3;
              const isPresentationStep = step >= 5;
              if (isPresentationStep && !areaOverlayFill) return null;

              return (
                <>
                  <Line
                    points={polygonPoints}
                    closed={polyClosed}
                    stroke={isPolygonEditStep ? "#00A3FF" : undefined}
                    strokeWidth={isPolygonEditStep ? 2 : 0}
                    fill={
                      polyClosed
                        ? isPolygonEditStep
                          ? "rgba(46,204,113,0.25)"
                          : areaOverlayFill
                        : undefined
                    }
                    listening={isPolygonEditStep}
                  />
                  {isPolygonEditStep &&
                    polygonPoints.map(
                      (_, i) =>
                        i % 2 === 0 && (
                          <Circle
                            key={i}
                            x={polygonPoints[i]}
                            y={polygonPoints[i + 1]}
                            radius={7}
                            fill="#FF6363"
                            stroke="#ffffff"
                            strokeWidth={2}
                          />
                        )
                    )}
                </>
              );
            })()}

          {/* Abdeckblöcke (v41): weiße Flächen für den PDF-Printout.
              Sie liegen über Plan und Flächenmarkierung, aber unter den
              Sensorpunkten und AURAs – ein verrutschter Block kann so nie
              einen Sensor verdecken. Bearbeitbar nur in Schritt 6. */}
          {coverBlocks.map((b) => (
            <Rect
              key={b.id}
              name={b.id}
              ref={(node) => {
                if (node) coverNodeRefs.current[b.id] = node;
                else delete coverNodeRefs.current[b.id];
              }}
              x={b.x}
              y={b.y}
              width={b.width}
              height={b.height}
              fill="#ffffff"
              listening={step === 6}
              draggable={step === 6}
              onMouseDown={() => {
                if (step === 6) setSelectedCoverId(b.id);
              }}
              onDragStart={() => setSelectedCoverId(b.id)}
              onDragMove={(e) => {
                /* Rahmen und Griffe laufen live mit – rein imperativ */
                if (b.id === selectedCoverId) {
                  syncCoverUi({
                    x: e.target.x(),
                    y: e.target.y(),
                    width: b.width,
                    height: b.height,
                  });
                  if (uiLayerRef.current) uiLayerRef.current.batchDraw();
                }
              }}
              onDragEnd={(e) =>
                commitCoverBlockChange(
                  b.id,
                  { x: e.target.x(), y: e.target.y() },
                  b
                )
              }
            />
          ))}

          {/* Auswahl‑Rechteck */}
          {(areaDeleteMode || moveSelectionMode) && selRect && (
            <Rect
              x={selRect.x}
              y={selRect.y}
              width={selRect.w}
              height={selRect.h}
              stroke={moveSelectionMode ? "#0066ff" : "#E55400"}
              strokeWidth={2}
              dash={[6, 4]}
              opacity={0.8}
              fill={
                moveSelectionMode
                  ? "rgba(0,102,255,0.12)"
                  : "rgba(229,84,0,0.15)"
              }
            />
          )}

          {/* Sensors – in einer verschiebbaren Group */}
          <Group
            x={rasterOffset.x}
            y={rasterOffset.y}
            draggable={
              rasterDraggable && !deleteMode && !addMode && !moveSelectionMode
            }
            onDragEnd={(e) => {
              if (!rasterDraggable) return;
              const prev = { ...rasterOffset };
              const next = { x: e.target.x(), y: e.target.y() };
              setRasterOffset(next);
              setHistory((h) => [...h, { type: "drag", prevOffset: prev }]);
              setRasterDraggable(false);
            }}
          >
            {showSensors &&
              bulkSensorItems.length > 0 &&
              Object.entries(
                bulkSensorItems.reduce((groups, pt) => {
                  const color = getSensorColor(pt.id);
                  if (!groups[color]) groups[color] = [];
                  groups[color].push(pt);
                  return groups;
                }, {})
              ).map(([color, pts]) => (
                <Group key={color}>
                  {/*
                    v26 Performance: Standard-Sensoren werden weiterhin gebündelt
                    gezeichnet, jetzt aber farbgruppiert. So bleiben eingefärbte
                    Sensoren performant, ohne tausende Einzel-Elemente zu erzeugen.
                  */}
                  <Shape
                    fill={color}
                    opacity={0.5}
                    listening={rasterDraggable}
                    sceneFunc={(ctx, shape) => {
                      ctx.beginPath();
                      pts.forEach((pt) => {
                        const off = sensorOffsets[pt.id] || { x: 0, y: 0 };
                        const cx = pt.x + off.x;
                        const cy = pt.y + off.y;
                        ctx.moveTo(cx + sensorOuterRadius, cy);
                        ctx.arc(
                          cx,
                          cy,
                          sensorOuterRadius,
                          0,
                          Math.PI * 2,
                          false
                        );
                      });
                      ctx.fillStrokeShape(shape);
                    }}
                    hitFunc={(ctx, shape) => {
                      ctx.beginPath();
                      pts.forEach((pt) => {
                        const off = sensorOffsets[pt.id] || { x: 0, y: 0 };
                        const cx = pt.x + off.x;
                        const cy = pt.y + off.y;
                        ctx.moveTo(cx + sensorOuterRadius, cy);
                        ctx.arc(
                          cx,
                          cy,
                          sensorOuterRadius,
                          0,
                          Math.PI * 2,
                          false
                        );
                      });
                      ctx.fillStrokeShape(shape);
                    }}
                  />
                  <Shape
                    fill={color}
                    listening={false}
                    sceneFunc={(ctx, shape) => {
                      ctx.beginPath();
                      pts.forEach((pt) => {
                        const off = sensorOffsets[pt.id] || { x: 0, y: 0 };
                        const cx = pt.x + off.x;
                        const cy = pt.y + off.y;
                        ctx.moveTo(cx + sensorInnerRadius, cy);
                        ctx.arc(
                          cx,
                          cy,
                          sensorInnerRadius,
                          0,
                          Math.PI * 2,
                          false
                        );
                      });
                      ctx.fillStrokeShape(shape);
                    }}
                  />
                </Group>
              ))}

            {showSensors &&
              (() => {
                const renderSensor = (pt, isSel) => {
                  // Im Dreh-Modus: gedrehte Position für Vorschau verwenden
                  const rotOff = getRotatedOffset(pt.id);
                  const off = rotOff || sensorOffsets[pt.id] || { x: 0, y: 0 };
                  const cx = pt.x + off.x;
                  const cy = pt.y + off.y;
                  const cursor = deleteMode
                    ? "pointer"
                    : addMode
                    ? "crosshair"
                    : moveSelectionMode
                    ? "move"
                    : "default";
                  const sensorName = isSel
                    ? `selected-sensor sensor-${pt.id}`
                    : `sensor-${pt.id}`;

                  const sensorColor = getSensorColor(pt.id);
                  const safeScale = Math.max(stageScale, 0.001);
                  const selectedStrokeWidth = Math.max(
                    0.6,
                    SELECTED_SENSOR_FRAME_STROKE_SCREEN / safeScale
                  );

                  return (
                    <Group key={pt.id} x={cx} y={cy} name={sensorName}>
                      <Circle
                        radius={sensorOuterRadius}
                        fill={sensorColor}
                        opacity={0.5}
                        name={sensorName}
                        cursor={cursor}
                      />
                      <Circle
                        radius={sensorInnerRadius}
                        fill={sensorColor}
                        name={sensorName}
                        cursor={cursor}
                      />
                      {isSel && (
                        <Circle
                          radius={sensorOuterRadius + 1.5 / safeScale}
                          stroke={SELECTED_SENSOR_FRAME_COLOR}
                          strokeWidth={selectedStrokeWidth}
                          opacity={0.95}
                          listening={false}
                        />
                      )}
                    </Group>
                  );
                };

                return (
                  <>
                    {selectedSensorItems.length > 0 && !rotateMode && (
                      <Group
                        name="selected-sensor selectedGroup"
                        draggable={moveSelectionMode}
                        onDragEnd={(e) => {
                          const dx = e.target.x();
                          const dy = e.target.y();
                          e.target.position({ x: 0, y: 0 });
                          if (dx === 0 && dy === 0) return;
                          const ids = [...selectedIds];
                          setSensorOffsets((prev) => {
                            const nxt = { ...prev };
                            ids.forEach((id) => {
                              const cur = nxt[id] || { x: 0, y: 0 };
                              nxt[id] = {
                                x: cur.x + dx,
                                y: cur.y + dy,
                              };
                            });
                            return nxt;
                          });
                          setHistory((h) => [
                            ...h,
                            { type: "moveSelection", ids, dx, dy },
                          ]);
                        }}
                      >
                        {renderSelectionBox()}
                        {selectedSensorItems.map((pt) =>
                          renderSensor(pt, true)
                        )}
                      </Group>
                    )}
                    {/* Im Dreh-Modus: Sensoren einzeln rendern (nicht in draggable Group) */}
                    {selectedSensorItems.length > 0 && rotateMode && (
                      <>
                        {renderSelectionBox()}
                        {selectedSensorItems.map((pt) =>
                          renderSensor(pt, true)
                        )}
                      </>
                    )}
                  </>
                );
              })()}

            {/* ─── Aura-Punkte ─── */}
            {AURA_FEATURE_ENABLED &&
              showSensors &&
              auraPoints.map((a, auraIndex) => {
                const auraColor = getAuraColor(a);
                const mpp = mPerPx || 0.01;
                const outerRadiusPx = (10 / mpp) * safeAuraVisualScale;
                const midRadiusPx = (3 / mpp) * safeAuraVisualScale;
                const auraNumber = auraIndex + 1;
                const numberText = String(auraNumber);
                /* v47: Der Nummernmodus bleibt bewusst deutlich sichtbar.
                   Der AURA-Regler skaliert Marker, Zahl und den kleinen
                   transparenten Orientierungskreis gemeinsam. */
                const baseNumberCircleRadius =
                  numberText.length >= 3
                    ? 20
                    : numberText.length === 2
                    ? 18
                    : 16;
                const numberCircleRadius =
                  baseNumberCircleRadius * safeAuraVisualScale;
                const numberHaloRadius =
                  (baseNumberCircleRadius + 14) * safeAuraVisualScale;
                const baseNumberFontSize =
                  numberText.length >= 3
                    ? 14
                    : numberText.length === 2
                    ? 15
                    : 16;
                const numberFontSize =
                  baseNumberFontSize * safeAuraVisualScale;
                const cursor = auraMode ? "pointer" : "move";
                const canDragAura =
                  !rasterDraggable &&
                  !areaDeleteMode &&
                  !addMode &&
                  !moveSelectionMode;
                return (
                  <Group
                    key={a.id}
                    x={a.x}
                    y={a.y}
                    draggable={canDragAura}
                    onDragEnd={(e) => {
                      const newX = e.target.x();
                      const newY = e.target.y();
                      if (newX === a.x && newY === a.y) return;
                      setAuraPoints((pts) =>
                        pts.map((p) =>
                          p.id === a.id ? { ...p, x: newX, y: newY } : p
                        )
                      );
                      setHistory((h) => [
                        ...h,
                        {
                          type: "auraMove",
                          id: a.id,
                          prevX: a.x,
                          prevY: a.y,
                        },
                      ]);
                    }}
                  >
                    {!auraNumbersEnabled ? (
                      <>
                        <Circle
                          radius={outerRadiusPx}
                          fill={auraColor}
                          opacity={0.15}
                          listening={false}
                        />
                        <Circle
                          radius={midRadiusPx}
                          fill={auraColor}
                          opacity={0.35}
                          listening={false}
                        />
                      </>
                    ) : (
                      <Circle
                        radius={numberHaloRadius}
                        fill={auraColor}
                        opacity={0.2}
                        listening={false}
                      />
                    )}
                    <Circle
                      radius={auraNumbersEnabled ? numberCircleRadius : 6}
                      fill={auraColor}
                      stroke="#ffffff"
                      strokeWidth={auraNumbersEnabled ? 1.5 : 1}
                      strokeScaleEnabled={false}
                      hitStrokeWidth={16}
                      name={a.id}
                      cursor={cursor}
                      listening={true}
                    />
                    {auraNumbersEnabled && (
                      <Text
                        x={-numberCircleRadius}
                        y={-numberCircleRadius}
                        width={numberCircleRadius * 2}
                        height={numberCircleRadius * 2}
                        text={numberText}
                        align="center"
                        verticalAlign="middle"
                        fill="#ffffff"
                        fontSize={numberFontSize}
                        fontStyle="bold"
                        fontFamily="Fira Sans, Arial, sans-serif"
                        listening={false}
                      />
                    )}
                  </Group>
                );
              })}
          </Group>
        </Layer>

        {/* UI-Layer (v42): nur in Schritt 6 gemountet – spart bei großen
            (HD-)Plänen dauerhaft eine volle Canvas-Ebene. Enthält die
            gestrichelten Rahmen der Abdeckblöcke und den Transformer;
            wird beim PDF-Export ausgeblendet und ist nie Teil des
            Printouts. */}
        {step === 6 && (
          <Layer ref={uiLayerRef}>
            {coverBlocks.map((b) =>
              b.id === selectedCoverId ? null : (
                <Rect
                  key={`ui-${b.id}`}
                  x={b.x}
                  y={b.y}
                  width={b.width}
                  height={b.height}
                  stroke="rgba(15, 161, 216, 0.55)"
                  strokeWidth={1.1}
                  strokeScaleEnabled={false}
                  dash={[6, 4]}
                  listening={false}
                />
              )
            )}
            {(() => {
              const sb = coverBlocks.find((b) => b.id === selectedCoverId);
              if (!sb) return null;
              const safeScale = Math.max(stageScale, 0.001);
              const handleSize = 14 / safeScale; // konstant ~14 px auf dem Bildschirm
              const corners = [
                { key: "nw", x: sb.x, y: sb.y, cursor: "nwse-resize" },
                {
                  key: "ne",
                  x: sb.x + sb.width,
                  y: sb.y,
                  cursor: "nesw-resize",
                },
                {
                  key: "sw",
                  x: sb.x,
                  y: sb.y + sb.height,
                  cursor: "nesw-resize",
                },
                {
                  key: "se",
                  x: sb.x + sb.width,
                  y: sb.y + sb.height,
                  cursor: "nwse-resize",
                },
              ];
              return (
                <>
                  <Rect
                    ref={(node) => {
                      coverUiRefs.current.outline = node;
                    }}
                    x={sb.x}
                    y={sb.y}
                    width={sb.width}
                    height={sb.height}
                    stroke="#0FA1D8"
                    strokeWidth={1.8}
                    strokeScaleEnabled={false}
                    dash={[6, 4]}
                    listening={false}
                  />
                  {corners.map((c) => (
                    <Rect
                      key={c.key}
                      name={`cover-handle-${c.key}`}
                      ref={(node) => {
                        coverUiRefs.current.handles[c.key] = node;
                      }}
                      x={c.x}
                      y={c.y}
                      width={handleSize}
                      height={handleSize}
                      offsetX={handleSize / 2}
                      offsetY={handleSize / 2}
                      fill="#0FA1D8"
                      stroke="#ffffff"
                      strokeWidth={2}
                      strokeScaleEnabled={false}
                      cornerRadius={handleSize * 0.18}
                      draggable
                      onMouseEnter={(e) => {
                        const st = e.target.getStage();
                        if (st) st.container().style.cursor = c.cursor;
                      }}
                      onMouseLeave={(e) => {
                        const st = e.target.getStage();
                        if (st) st.container().style.cursor = "";
                      }}
                      onDragStart={() => {
                        coverGestureRef.current = {
                          x: sb.x,
                          y: sb.y,
                          width: sb.width,
                          height: sb.height,
                        };
                      }}
                      onDragMove={(e) => applyCoverResize(sb, c.key, e.target)}
                      onDragEnd={() => finishCoverResize(sb)}
                    />
                  ))}
                </>
              );
            })()}
          </Layer>
        )}
      </Stage>
    );
  };

  /* ---------------- Render ---------------- */
  return (
    <>
      {isFileLoading && (
        <div className="file-loading-overlay" role="status" aria-live="polite">
          <div className="file-loading-card">
            <div className="file-loading-logo-ring">
              <img
                src="https://www.hum-id.com/vertrieb/logowhite.png"
                alt="HUM-ID"
                className="file-loading-logo"
              />
            </div>
            <div className="file-loading-title">Plan wird geladen…</div>
            <div className="file-loading-text">
              Bitte kurz warten, die Datei wird vorbereitet.
            </div>
          </div>
        </div>
      )}

      {/* ─── HUM-ID Webseite Badge ─────── */}
      <a
        href="https://www.hum-id.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "fixed",
          top: "8px",
          right: "12px",
          zIndex: 9999,
          background: "#A6C63B",
          color: "#fff",
          padding: "6px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "bold",
          fontFamily: "FiraSans, sans-serif",
          letterSpacing: "0.3px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
          textDecoration: "none",
          cursor: "pointer",
        }}
      >
        zur HUM-ID Webseite
      </a>

      {/* ─── Fixer Header ────────────────── */}
      <header
        className={`header-fixed${headerCollapsed ? " header-collapsed" : ""}`}
        ref={headerRef}
      >
        {!headerCollapsed && (
          <div className="header-inner">
            <h1>HUM‑ID Sensor-Planer</h1>
            <img
              src="https://www.hum-id.com/vertrieb/logowhite.png"
              alt="HUM‑ID Logo"
              className="humid-logo"
            />
            <ol
              className="step-indicator"
              aria-label="Fortschritt im Sensor-Planer"
            >
              {STEP_LABELS.map((label, index) => {
                const stepNumber = index + 1;
                const isDone = step > stepNumber;
                const isActive = step === stepNumber;
                return (
                  <li
                    key={label}
                    className={`step-indicator-item${
                      isActive ? " is-active" : ""
                    }${isDone ? " is-done" : ""}`}
                    aria-current={isActive ? "step" : undefined}
                  >
                    <span className="step-indicator-dot" aria-hidden="true">
                      {isDone ? "✓" : stepNumber}
                    </span>
                    <span className="step-indicator-label">{label}</span>
                  </li>
                );
              })}
            </ol>
            <div className="step-block">
              {step === 1 && (
                <>
                  <p className="step-main">
                    <b>Schritt 1:</b> Bitte wählen Sie eine Grafik aus,
                    z.&nbsp;B.&nbsp;eine Dachaufsicht oder einen Grundriss.
                  </p>
                  <p className="step-info">
                    Format: <strong>PDF, PNG oder JPG.</strong> Hinweis: Ab
                    einer Dateigröße von 4&nbsp;MB wird der Plan komprimiert.
                  </p>
                </>
              )}

              {step === 2 && (
                <>
                  <p className="step-main">
                    <b>Schritt 2:</b> Legen Sie den Maßstab fest.
                  </p>
                  <p className="step-info">
                    Suchen Sie sich einen Maßstab, klicken Sie mit der Maus auf
                    den Startpunkt und auf den Endpunkt des Maßstabs und nennen
                    Sie den Abstand.
                  </p>
                </>
              )}

              {step === 3 && (
                <>
                  <p className="step-main">
                    <b>Schritt 3:</b> Definieren Sie die Fläche, auf der die
                    Sensoren verlegt werden sollen.
                  </p>
                  <p className="step-info">
                    Erstellen Sie per Mausklicks ein Polygon, sobald Sie wieder
                    auf den ersten&nbsp;Punkt klicken, ist die Fläche
                    geschlossen und färbt sich grün.
                  </p>
                </>
              )}

              {step === 4 && (
                <>
                  <p className="step-main">
                    <b>Schritt 4:</b> Wählen Sie ein Sensor-Raster aus.
                  </p>
                  <p className="step-info">
                    Für kleine Flächen bis 200m² empfehlen wir ein 1x1m² Raster,
                    für mittlere Flächen ein 1x2m² Raster, bei sehr große
                    Flächen über 1.000m² empfiehlt sich ein 1x4m² Raster.
                  </p>
                </>
              )}

              {step === 5 && (
                <>
                  <p className="step-main step-main-with-info">
                    <b>Schritt 5:</b> Detailanpassungen und Download.
                    <span
                      className="step5-info-icon-wrapper"
                      onClick={() => setShowStep5Info((v) => !v)}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM11 15h2v2h-2v-2zm0-8h2v6h-2V7z"
                        />
                      </svg>
                      {showStep5Info && (
                        <div className="step5-info-popover">
                          Wenn Sie das gesamte Grid verschieben möchten, wählen
                          Sie zunächst "Raster verschieben", klicken und halten
                          Sie dann einen Punkt des Rasters und verschieben Sie
                          es. Sie können auch Bereiche mit Sensorpunkten löschen
                          (z.B. bei Dachfenstern oder Photovoltaikpanelen) oder
                          einzelne neue Sensorpunkte hinzufügen. Im
                          "Auswahl"-Modus wird die Planunterlage leicht
                          abgedunkelt. Ziehen Sie ein Rechteck um Sensoren, um
                          diese zu markieren, zu verschieben oder per
                          Rechtsklick zu kopieren, zu drehen oder zu löschen.
                        </div>
                      )}
                    </span>
                  </p>
                </>
              )}
              {step === 6 && (
                <>
                  <p className="step-main">
                    <b>Schritt 6:</b> Druck &amp; Export – gestalten Sie Ihren
                    PDF-Printout.
                  </p>
                  <p className="step-info">
                    Projektangaben und Produktübersicht legen Sie im Panel
                    rechts fest. Weiße Abdeckblöcke verschieben Sie direkt auf
                    dem Plan und skalieren sie über die Eckpunkte.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        <nav className="action-bar" style={{ position: "relative" }}>
          <button
            type="button"
            className="header-collapse-btn"
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              width: 30,
              height: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255, 255, 255, 0.14)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              borderRadius: 8,
              color: "#ffffff",
              cursor: "pointer",
              zIndex: 5,
              padding: 0,
            }}
            onClick={() => setHeaderCollapsed((c) => !c)}
            title={
              headerCollapsed
                ? "Kopfbereich einblenden"
                : "Kopfbereich ausblenden – mehr Platz für den Plan"
            }
            aria-label={
              headerCollapsed
                ? "Kopfbereich einblenden"
                : "Kopfbereich ausblenden"
            }
            aria-expanded={!headerCollapsed}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {headerCollapsed ? (
                <polyline points="6 9 12 15 18 9" />
              ) : (
                <polyline points="6 15 12 9 18 15" />
              )}
            </svg>
          </button>
          <div className="action-inner">
            <div className="actions">
              {/* === Step 1 ================== */}
              {step === 1 && (
                <>
                  <label className="file-label">
                    <svg
                      className="upload-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M12 16V4"
                        stroke="#0A2348"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M6 10l6-6 6 6"
                        stroke="#0A2348"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 20h16"
                        stroke="#0A2348"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Datei auswählen
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleFileUpload}
                      className="file-input"
                    />
                  </label>
                  <label
                    className="file-label special-file-label"
                    title="PDF in hoher Auflösung ohne JPEG-Kompression laden – schärfster Druck, aber langsamere Bearbeitung. Für den Normalfall besser „Datei auswählen“."
                  >
                    <svg
                      className="upload-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M4 4h10l6 6v10H4z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 4v6h6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 16h8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Upload HD
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) =>
                        handleFileUpload(e, { specialPdf: true })
                      }
                      className="file-input"
                    />
                  </label>
                  <label className="file-label project-file-label">
                    <svg
                      className="upload-icon"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M4 4h16v16H4z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 8h8M8 12h8M8 16h5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Projekt öffnen
                    <input
                      ref={projectInputRef}
                      type="file"
                      accept=".humidplan,application/json"
                      onChange={handleProjectFileOpen}
                      className="file-input"
                    />
                  </label>
                  {autosaveAvailable && (
                    <Btn onClick={handleRestoreAutosave} variant="project">
                      Auto-Sicherung laden
                    </Btn>
                  )}
                  <span className="file-name">
                    {fileName || "Keine ausgewählt"}
                  </span>
                  <Btn
                    onClick={() => setStep(2)}
                    disabled={!imageObj}
                    variant="next"
                  >
                    Weiter
                  </Btn>
                </>
              )}

              {/* === Step 2 ======================= */}
              {step === 2 && (
                <>
                  <Btn
                    onClick={() => setStep(1)}
                    aria-label="Zurück zu Schritt 1"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </Btn>
                  {scalePoints.length === 4 && (
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Distanz (m)"
                      value={realDistance}
                      onChange={(e) => setRealDistance(e.target.value)}
                      className="distance-input"
                    />
                  )}
                  <Btn
                    onClick={undoScalePoint}
                    disabled={scalePoints.length === 0}
                  >
                    Undo
                  </Btn>
                  <Btn
                    onClick={applyScale}
                    disabled={!realDistance}
                    variant="next"
                  >
                    Maßstab übernehmen
                  </Btn>
                </>
              )}

              {/* === Step 3 ======================= */}
              {step === 3 && (
                <>
                  <Btn
                    onClick={() => setStep(2)}
                    aria-label="Zurück zu Schritt 2"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </Btn>
                  <Btn
                    onClick={undoLast}
                    disabled={polygonPoints.length === 0 || polyClosed}
                  >
                    Rückgängig
                  </Btn>
                  <Btn
                    onClick={resetPoly}
                    disabled={polygonPoints.length === 0}
                  >
                    Zurücksetzen
                  </Btn>
                  <Btn
                    onClick={finalizePoly}
                    disabled={!polyClosed}
                    variant="next"
                  >
                    Sensorfläche übernehmen
                  </Btn>
                  {polygonPoints.length === 0 && (
                    <span className="action-bar-hint">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginRight: "8px" }}
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM11 15h2v2h-2v-2zm0-8h2v6h-2V7z"
                        />
                      </svg>{" "}
                      Erstellen Sie durch Klicks in der Karte eine geschlossene
                      Fläche
                    </span>
                  )}
                </>
              )}

              {/* === Step 4 ================= */}
              {step === 4 && (
                <>
                  <Btn
                    onClick={() => setStep(3)}
                    aria-label="Zurück zu Schritt 3"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </Btn>
                  <select
                    value={sensorRaster}
                    onChange={(e) => setSensorRaster(e.target.value)}
                    className="select-raster"
                  >
                    {SENSOR_RASTER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <span className="area-size-hint">
                    Aktuelle Sensorfläche:{" "}
                    <b>
                      {areaM2 != null
                        ? areaM2.toFixed(1).replace(".", ",")
                        : "–"}{" "}
                      m²
                    </b>
                  </span>
                  <Btn onClick={() => setStep(5)} variant="next">
                    Weiter
                  </Btn>
                </>
              )}

              {/* === Step 5 ====================== */}
              {step === 5 && (
                <>
                  <Btn
                    onClick={() => setStep(4)}
                    aria-label="Zurück zu Schritt 4"
                    disabled={
                      areaDeleteMode ||
                      addMode ||
                      rasterDraggable ||
                      moveSelectionMode ||
                      auraMode
                    }
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </Btn>
                  <div className="info-wrapper">
                    <Btn
                      onClick={toggleRasterDrag}
                      disabled={
                        areaDeleteMode ||
                        addMode ||
                        moveSelectionMode ||
                        auraMode
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <line x1="12" y1="2" x2="12" y2="22"></line>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                        <polyline points="19 9 12 2 5 9"></polyline>
                        <polyline points="19 15 12 22 5 15"></polyline>
                        <polyline points="9 5 2 12 9 19"></polyline>
                        <polyline points="15 5 22 12 15 19"></polyline>
                      </svg>
                      {rasterDraggable
                        ? "Raster fixieren"
                        : "Raster verschieben"}
                    </Btn>
                    <span className="tooltip">
                      Klicken und halten Sie einen Sensor im Raster, lassen Sie
                      los, um das gesamte Sensorraster zu platzieren.
                    </span>
                  </div>
                  <div className="info-wrapper">
                    <Btn
                      onClick={() => {
                        const newAreaDeleteMode = !areaDeleteMode;
                        setAreaDeleteMode(newAreaDeleteMode);
                        setSelRect(null);
                        if (newAreaDeleteMode) {
                          setRasterDraggable(false);
                          setAddMode(false);
                          setAddPlacementMode("single");
                          setMoveSelectionMode(false);
                          setSelectedIds([]);
                          setAuraMode(false);
                        }
                      }}
                      disabled={
                        rasterDraggable ||
                        addMode ||
                        moveSelectionMode ||
                        auraMode
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <circle cx="6" cy="6" r="3"></circle>
                        <circle cx="6" cy="18" r="3"></circle>
                        <line x1="20" y1="4" x2="8.12" y2="15.88"></line>
                        <line x1="14.47" y1="14.48" x2="20" y2="20"></line>
                        <line x1="8.12" y1="8.12" x2="12" y2="12"></line>
                      </svg>
                      {areaDeleteMode
                        ? "Bereich-Modus beenden"
                        : "Bereich löschen"}
                    </Btn>
                    <span className="tooltip">
                      Zeichnen Sie mit der Maus ein Rechteck um die Sensoren,
                      die Sie löschen möchten (z.B. bei Dachfenstern oder
                      Photovoltaikanlagen).
                    </span>
                  </div>
                  {/* === Auswahl ========================= */}
                  <div className="info-wrapper">
                    <Btn
                      onClick={() => {
                        const next = !moveSelectionMode;
                        setMoveSelectionMode(next);
                        setSelectedIds([]);
                        setSelRect(null);
                        // Beim Deaktivieren auch Dreh-/Paste-Modus beenden
                        setRotateMode(false);
                        setRotateAngle(0);
                        setRotateCenter(null);
                        setPasteMode(false);
                        setContextMenu(null);
                        if (next) {
                          setRasterDraggable(false);
                          setAreaDeleteMode(false);
                          setAddMode(false);
                          setAddPlacementMode("single");
                          setAuraMode(false);
                        }
                      }}
                      disabled={
                        rasterDraggable || addMode || areaDeleteMode || auraMode
                      }
                      className={moveSelectionMode ? "btn-active" : ""}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <rect
                          x="3"
                          y="3"
                          width="8"
                          height="8"
                          rx="1"
                          strokeDasharray="2 2"
                        ></rect>
                        <polyline points="14 7 17 7 17 10"></polyline>
                        <polyline points="21 14 21 17 18 17"></polyline>
                        <line x1="13" y1="13" x2="20" y2="20"></line>
                        <polyline points="20 20 17 20 20 17"></polyline>
                      </svg>
                      {moveSelectionMode ? "Auswahl beenden" : "Auswahl"}
                    </Btn>
                    <span className="tooltip">
                      Ziehen Sie ein Rechteck um Sensoren oder klicken Sie einen
                      einzelnen Sensor an. Mit Shift/Cmd/Ctrl + Klick ergänzen
                      Sie weitere Sensoren. Rechtsklick für Kopieren,
                      Duplizieren, Drehen oder Entfernen.
                    </span>
                  </div>
                  {moveSelectionMode && selectedIds.length > 0 && (
                    <div className="info-wrapper">
                      <Btn
                        onClick={handleDeleteSelected}
                        disabled={rotateMode}
                        variant="undo"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: "8px" }}
                        >
                          <path d="M3 6h18" />
                          <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                        </svg>
                        Entfernen
                      </Btn>
                      <span className="tooltip">
                        Entfernt die aktuelle Auswahl. Alternativ können Sie die
                        Entfernen-/Delete-Taste verwenden.
                      </span>
                    </div>
                  )}
                  <div className="info-wrapper">
                    <Btn
                      onClick={() => {
                        const newAddMode = !addMode;
                        setAddMode(newAddMode);
                        if (newAddMode) {
                          setAddPlacementMode("single");
                          setRasterDraggable(false);
                          setAreaDeleteMode(false);
                          setSelRect(null);
                          setMoveSelectionMode(false);
                          setSelectedIds([]);
                          setAuraMode(false);
                        } else {
                          setAddPlacementMode("single");
                        }
                      }}
                      disabled={
                        areaDeleteMode ||
                        rasterDraggable ||
                        moveSelectionMode ||
                        auraMode
                      }
                      className={
                        addMode && addPlacementMode === "single"
                          ? "btn-active"
                          : ""
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                      </svg>
                      {addMode
                        ? "Hinzufügemodus beenden"
                        : "Sensoren hinzufügen"}
                    </Btn>
                    <span className="tooltip">
                      Einzelmodus: Klicken Sie auf den Plan, um einen Sensor
                      hinzuzufügen. Die +100-Optionen setzen direkt ganze
                      Sensorblöcke.
                    </span>
                  </div>
                  {addMode && (
                    <>
                      <div className="info-wrapper">
                        <Btn
                          onClick={() => setAddPlacementMode("block-1x1")}
                          className={
                            addPlacementMode === "block-1x1"
                              ? "btn-active add-batch-active"
                              : "add-batch-btn"
                          }
                        >
                          +100 (1x1)
                        </Btn>
                        <span className="tooltip">
                          Setzt beim nächsten Klick 10×10 Sensoren im
                          1×1-m-Raster. Der Block wird anschließend automatisch
                          markiert.
                        </span>
                      </div>
                      <div className="info-wrapper">
                        <Btn
                          onClick={() => setAddPlacementMode("block-1x2")}
                          className={
                            addPlacementMode === "block-1x2"
                              ? "btn-active add-batch-active"
                              : "add-batch-btn"
                          }
                        >
                          +100 (1x2)
                        </Btn>
                        <span className="tooltip">
                          Setzt beim nächsten Klick 10×10 Sensoren im
                          1×2-m-Raster. Der Block wird anschließend automatisch
                          markiert.
                        </span>
                      </div>
                    </>
                  )}
                  {/* === Aura ========================= */}
                  {AURA_FEATURE_ENABLED && (
                    <div className="info-wrapper">
                      <Btn
                        onClick={() => {
                          const next = !auraMode;
                          setAuraMode(next);
                          if (next) {
                            setRasterDraggable(false);
                            setAreaDeleteMode(false);
                            setSelRect(null);
                            setAddMode(false);
                            setAddPlacementMode("single");
                            setMoveSelectionMode(false);
                            setSelectedIds([]);
                          }
                        }}
                        disabled={
                          rasterDraggable ||
                          addMode ||
                          areaDeleteMode ||
                          moveSelectionMode
                        }
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: "8px" }}
                        >
                          <circle cx="12" cy="12" r="3"></circle>
                          <circle cx="12" cy="12" r="7" opacity="0.6"></circle>
                          <circle cx="12" cy="12" r="11" opacity="0.3"></circle>
                        </svg>
                        {auraMode ? "Aura-Modus beenden" : "Aura hinzufügen"}
                      </Btn>
                      <span className="tooltip">
                        Klicken Sie in den Plan, um eine Aura (blaue Reichweite,
                        10 m Radius) zu setzen. Klick auf den blauen Mittelpunkt
                        einer bestehenden Aura löscht sie.
                      </span>
                    </div>
                  )}
                  <div className="info-wrapper">
                    <Btn
                      onClick={undoLastAction}
                      disabled={history.length === 0 || rasterDraggable}
                      aria-label="Letzte Aktion rückgängig machen"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 5L8 9l4 4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 9h5a5 5 0 110 10h-1"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </Btn>
                    <span className="tooltip">
                      Macht die letzte Aktion rückgängig.
                    </span>
                  </div>
                  <div className="info-wrapper">
                    <Btn
                      onClick={resetAppStates}
                      disabled={
                        areaDeleteMode ||
                        addMode ||
                        rasterDraggable ||
                        moveSelectionMode ||
                        auraMode
                      }
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginRight: "8px" }}
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="12" y1="18" x2="12" y2="12"></line>
                        <line x1="9" y1="15" x2="15" y2="15"></line>
                      </svg>
                      Neu starten
                    </Btn>
                    <span className="tooltip">
                      Setzt den Planer zurück und startet ein neues Projekt.
                    </span>
                  </div>
                  <div
                    className="sensor-size-control"
                    title="Sensorpunkt-Größe anpassen"
                  >
                    <span className="sensor-size-label">Sensorgröße</span>
                    <input
                      type="range"
                      min={SENSOR_VISUAL_SCALE_MIN}
                      max={SENSOR_VISUAL_SCALE_MAX}
                      step={SENSOR_VISUAL_SCALE_STEP}
                      value={safeSensorVisualScale}
                      onChange={(e) =>
                        setSensorVisualScale(parseFloat(e.target.value) || 1)
                      }
                      aria-label="Sensorpunkt-Größe"
                    />
                  </div>
                  {AURA_FEATURE_ENABLED && (
                    <div
                      className="aura-visual-control"
                      title="AURA-Darstellungsgröße anpassen oder AURA-Messpunkte nummerieren"
                    >
                      <span className="aura-visual-label">Aura-Größe</span>
                      <input
                        className="aura-size-slider"
                        type="range"
                        min={AURA_VISUAL_SCALE_MIN}
                        max={AURA_VISUAL_SCALE_MAX}
                        step={AURA_VISUAL_SCALE_STEP}
                        value={safeAuraVisualScale}
                        onChange={(e) =>
                          setAuraVisualScale(parseFloat(e.target.value) || 1)
                        }
                        aria-label="AURA-Darstellungsgröße"
                      />
                      <label className="aura-number-toggle">
                        <input
                          type="checkbox"
                          checked={auraNumbersEnabled}
                          onChange={(e) =>
                            setAuraNumbersEnabled(e.target.checked)
                          }
                        />
                        <span>Aura-Nummern</span>
                      </label>
                    </div>
                  )}
                </>
              )}
              {/* === Step 6 ====================== */}
              {step === 6 && (
                <>
                  <Btn
                    onClick={() => setStep(5)}
                    aria-label="Zurück zu Schritt 5"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </Btn>
                  <span className="action-bar-hint">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginRight: "8px" }}
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM11 15h2v2h-2v-2zm0-8h2v6h-2V7z"
                      />
                    </svg>{" "}
                    Abdeckblöcke direkt auf dem Plan bearbeiten – alle weiteren
                    Einstellungen im Panel rechts.
                  </span>
                </>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* ─── Content ─── */}
      <div
        className="container"
        onMouseDown={handleSelectionBackgroundMouseDown}
      >
        <div
          className="content-wrapper"
          onMouseDown={handleSelectionBackgroundMouseDown}
        >
          {step === 1 && (
            <div className="space-y-4 border p-6 bg-white rounded start-card">
              <h2>Neues Projekt starten oder vorhandenes Projekt öffnen</h2>
              <p>
                Laden Sie eine Dachaufsicht als PDF, PNG oder JPG hoch – oder
                öffnen Sie eine gespeicherte <strong>.humidplan</strong>-Datei,
                um an einer früheren Sensorplatzierung weiterzuarbeiten.
              </p>
              {autosaveAvailable && (
                <p className="autosave-note">
                  Lokale Auto-Sicherung gefunden
                  {autosaveTimeLabel
                    ? ` · zuletzt ${autosaveTimeLabel} Uhr`
                    : ""}
                  .
                </p>
              )}
              {projectNotice && (
                <p className="project-notice">{projectNotice}</p>
              )}
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4 border p-6 bg-white rounded">
              <div
                ref={containerRef}
                className="relative w-full h-[70vh] overflow-hidden stage-wrapper"
              >
                <ZoomStage showSensors={false} />
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4 border p-6 bg-white rounded">
              <div
                ref={containerRef}
                className="relative w-full h-[70vh] overflow-hidden stage-wrapper"
              >
                <ZoomStage showSensors={false} />
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4 border p-6 bg-white rounded"></div>
          )}
          {step === 5 && (
            <div
              className="space-y-4 border p-6 bg-white rounded plan-card"
              onMouseDown={handleSelectionBackgroundMouseDown}
            >
              <div
                ref={containerRef}
                className="relative w-full h-[70vh] overflow-hidden stage-wrapper"
              >
                <ZoomStage showSensors={step === 5} />
              </div>
            </div>
          )}
          {step === 6 && (
            <div className="print-step-layout" ref={printLayoutRef}>
              <div className="space-y-4 border p-6 bg-white rounded plan-card print-stage-card">
                <div
                  ref={containerRef}
                  className="relative w-full h-[70vh] overflow-hidden stage-wrapper"
                >
                  <ZoomStage showSensors={true} />
                </div>
              </div>
              <aside
                className="print-panel"
                aria-label="Einstellungen für Druck und Export"
                ref={printPanelRef}
                style={
                  printPanelPos
                    ? {
                        left: printPanelPos.x,
                        top: printPanelPos.y,
                        right: "auto",
                      }
                    : undefined
                }
              >
                <div
                  className="print-panel-drag"
                  onMouseDown={startPrintPanelDrag}
                  title="Panel verschieben"
                >
                  <h2 className="print-panel-title">Druck &amp; Export</h2>
                  <svg
                    className="print-panel-grip"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <circle cx="7" cy="6" r="1.7" />
                    <circle cx="17" cy="6" r="1.7" />
                    <circle cx="7" cy="12" r="1.7" />
                    <circle cx="17" cy="12" r="1.7" />
                    <circle cx="7" cy="18" r="1.7" />
                    <circle cx="17" cy="18" r="1.7" />
                  </svg>
                </div>
                <p className="print-panel-sub">
                  Alle Elemente sind optional, werden mit dem Projekt
                  gespeichert und erscheinen nur im PDF, wenn sie aktiviert
                  sind. Das Panel lässt sich an der Kopfzeile verschieben.
                </p>

                <section className="print-section">
                  <header className="print-section-header">
                    <span className="print-section-title">Projektangaben</span>
                    <label className="print-toggle">
                      <input
                        type="checkbox"
                        checked={printShow.header}
                        onChange={() => togglePrintShow("header")}
                        aria-label="Projektangaben im PDF anzeigen"
                      />
                      <span
                        className="print-toggle-slider"
                        aria-hidden="true"
                      />
                    </label>
                  </header>
                  {printShow.header && (
                    <div className="print-fields">
                      <label className="print-field">
                        <span>Projektüberschrift</span>
                        <input
                          type="text"
                          value={printFields.title}
                          onChange={(e) =>
                            setPrintField("title", e.target.value)
                          }
                          placeholder="z. B. Sanierung Dachfläche Halle 3"
                        />
                      </label>
                      <label className="print-field">
                        <span>Untertitel</span>
                        <input
                          type="text"
                          value={printFields.subtitle}
                          onChange={(e) =>
                            setPrintField("subtitle", e.target.value)
                          }
                          placeholder="z. B. Verlegeempfehlung HUM-ID Sensorik"
                        />
                      </label>
                      <label className="print-field">
                        <span>Ersteller/in</span>
                        <input
                          type="text"
                          value={printFields.author}
                          onChange={(e) =>
                            setPrintField("author", e.target.value)
                          }
                          placeholder="Name"
                        />
                      </label>
                      <div className="print-field-row">
                        <label className="print-field">
                          <span>Erstelldatum</span>
                          <input
                            type="text"
                            value={printFields.date}
                            onChange={(e) =>
                              setPrintField("date", e.target.value)
                            }
                            placeholder="TT.MM.JJJJ"
                          />
                        </label>
                        <label className="print-field">
                          <span>Version</span>
                          <input
                            type="text"
                            value={printFields.version}
                            onChange={(e) =>
                              setPrintField("version", e.target.value)
                            }
                            placeholder="1.0"
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </section>

                <section className="print-section">
                  <header className="print-section-header">
                    <span className="print-section-title">
                      Produktübersicht
                    </span>
                    <label className="print-toggle">
                      <input
                        type="checkbox"
                        checked={printShow.products}
                        onChange={() => togglePrintShow("products")}
                        aria-label="Produktübersicht im PDF anzeigen"
                      />
                      <span
                        className="print-toggle-slider"
                        aria-hidden="true"
                      />
                    </label>
                  </header>
                  {printShow.products && (
                    <div className="print-products">
                      {PDF_PRODUCT_CATALOG.map((product) => {
                        const checked = printProducts.includes(product.id);
                        return (
                          <div key={product.id} className="print-product-row">
                            <label className="print-product-check">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePrintProduct(product.id)}
                              />
                              <span>{product.fullLabel}</span>
                            </label>
                            {checked && (
                              <input
                                type="text"
                                inputMode="numeric"
                                className="print-product-count"
                                value={printProductCounts[product.id] ?? ""}
                                onChange={(e) =>
                                  setPrintProductCount(
                                    product.id,
                                    e.target.value
                                  )
                                }
                                placeholder="Stk."
                                aria-label={`Anzahl ${product.fullLabel}`}
                                title="Anzahl – erscheint als Label auf dem Produktbild"
                              />
                            )}
                          </div>
                        );
                      })}
                      <p className="print-hint">
                        Die Anzahl erscheint als kleines Label auf dem
                        Produktbild im PDF-Kopf. Reihenfolge im PDF =
                        Reihenfolge dieser Liste.
                      </p>
                    </div>
                  )}
                </section>

                <section className="print-section">
                  <header className="print-section-header">
                    <span className="print-section-title">Abdeckblöcke</span>
                    <button
                      type="button"
                      className="print-add-block-btn"
                      onClick={addCoverBlock}
                      disabled={!imageObj}
                    >
                      + Block
                    </button>
                  </header>
                  {coverBlocks.length === 0 ? (
                    <p className="print-hint">
                      Weiße Blöcke verdecken störende Bereiche des
                      Originalplans, z.&nbsp;B. Legenden oder Randnotizen.
                      Blöcke lassen sich direkt auf dem Plan verschieben und
                      über die blauen Eckpunkte skalieren; die Entf-Taste löscht
                      den ausgewählten Block.
                    </p>
                  ) : (
                    <ul className="print-cover-list">
                      {coverBlocks.map((block, index) => (
                        <li
                          key={block.id}
                          className={
                            block.id === selectedCoverId ? "is-selected" : ""
                          }
                        >
                          <button
                            type="button"
                            className="print-cover-select"
                            onClick={() => setSelectedCoverId(block.id)}
                          >
                            Abdeckblock {index + 1}
                          </button>
                          <button
                            type="button"
                            className="print-cover-remove"
                            onClick={() => removeCoverBlock(block.id)}
                            aria-label={`Abdeckblock ${index + 1} entfernen`}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="print-section">
                  <header className="print-section-header">
                    <span className="print-section-title">
                      Logo &amp; Fußzeile
                    </span>
                  </header>
                  <div className="print-toggle-row">
                    <span>HUM-ID Logo</span>
                    <label className="print-toggle">
                      <input
                        type="checkbox"
                        checked={printShow.logo}
                        onChange={() => togglePrintShow("logo")}
                        aria-label="HUM-ID Logo im PDF anzeigen"
                      />
                      <span
                        className="print-toggle-slider"
                        aria-hidden="true"
                      />
                    </label>
                  </div>
                  <div className="print-toggle-row">
                    <span>Projektkennzahlen in der Fußzeile</span>
                    <label className="print-toggle">
                      <input
                        type="checkbox"
                        checked={printShow.stats}
                        onChange={() => togglePrintShow("stats")}
                        aria-label="Projektkennzahlen im PDF anzeigen"
                      />
                      <span
                        className="print-toggle-slider"
                        aria-hidden="true"
                      />
                    </label>
                  </div>
                  {printShow.stats && (
                    <div className="print-fields print-stats-fields">
                      <label className="print-field">
                        <span>Text Sensorfläche</span>
                        <input
                          type="text"
                          value={printFields.statsArea}
                          onChange={(e) =>
                            setPrintField("statsArea", e.target.value)
                          }
                          placeholder="z. B. 955 m² Sensorfläche"
                        />
                      </label>
                      <label className="print-field">
                        <span>Text Raster</span>
                        <input
                          type="text"
                          value={printFields.statsRaster}
                          onChange={(e) =>
                            setPrintField("statsRaster", e.target.value)
                          }
                          placeholder="z. B. 1 Sensor pro 2 m²"
                        />
                      </label>
                    </div>
                  )}
                </section>

                <div className="print-panel-stats">
                  {areaM2?.toFixed(1) || "–"} m² · {currentSensorCount} Sensoren
                  {AURA_FEATURE_ENABLED ? ` · ${auraPoints.length} AURA` : ""}
                </div>

                <div className="print-panel-actions">
                  <button
                    type="button"
                    className="print-preview-btn"
                    onClick={handlePdfPreview}
                    disabled={isPdfBusy}
                  >
                    {isPdfBusy ? "Bitte warten…" : "Vorschau"}
                  </button>
                  <button
                    type="button"
                    className="print-download-btn"
                    onClick={() => handleDownload({ withFrame: true })}
                    disabled={isPdfBusy}
                  >
                    PDF herunterladen
                  </button>
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>

      {/* +++ POLYGON HILFE-POPOVER +++ */}
      {showPolygonHelpPopover && (
        <div className="polygon-help-popover-overlay">
          <div className="polygon-help-popover-content">
            <h2>Fläche definieren</h2>
            <p>
              Erstellen Sie per Mausklicks eine Fläche. Sobald Sie wieder auf
              den ersten Punkt klicken, ist die Fläche geschlossen und färbt
              sich grün.
            </p>
            <div className="gif-container">
              <img
                src={`https://www.hum-id.com/new/polygonanimation.gif`}
                alt="Animation zum Zeichnen eines Polygons"
                className="polygon-gif"
              />
            </div>
            <button
              onClick={handleClosePolygonHelpPopover}
              className="polygon-help-cta-btn"
            >
              Alles klar
            </button>
            <button
              onClick={handleDontShowPolygonHelpAgain}
              className="polygon-help-dont-show-btn"
            >
              Nicht wieder anzeigen
            </button>
          </div>
        </div>
      )}

      {/* +++ ANGEBOTS-MODAL +++ */}
      {showOfferModal && (
        <div className="offer-modal-overlay">
          <div className="offer-modal-content">
            <button
              className="offer-modal-close-btn"
              onClick={handleCloseOfferModal}
              aria-label="Dialog schließen"
            >
              ×
            </button>
            <h2>Unverbindliches Angebot anfordern</h2>
            <p>
              Fordern Sie jetzt ein unverbindliches Angebot für die von Ihnen
              berechneten
              <strong>
                {" "}
                {sensorPoints.length +
                  addedPoints.length -
                  deletedIds.length}{" "}
                Sensoren{" "}
              </strong>
              an.
            </p>
            <a href={buildOfferMailto()} className="offer-modal-cta-btn">
              Angebot per E-Mail anfordern
            </a>
            <p className="offer-modal-mail-note">
              Öffnet Ihr E-Mail-Programm mit einer vorbereiteten Preisanfrage an
              info@hum-id.com – inklusive der Kennzahlen aus Ihrer Planung.
            </p>
          </div>
        </div>
      )}

      {/* +++ PDF-VORSCHAU +++ */}
      {pdfPreviewUrl && (
        <div
          className="pdf-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="PDF-Vorschau"
        >
          <div className="pdf-preview-content">
            <div className="pdf-preview-header">
              <span className="pdf-preview-title">PDF-Vorschau</span>
              <div className="pdf-preview-header-actions">
                <button
                  type="button"
                  className="pdf-preview-download-btn"
                  onClick={() => {
                    closePdfPreview();
                    handleDownload({ withFrame: true });
                  }}
                >
                  PDF herunterladen
                </button>
                <button
                  type="button"
                  className="pdf-preview-close-btn"
                  onClick={closePdfPreview}
                  aria-label="Vorschau schließen"
                >
                  ×
                </button>
              </div>
            </div>
            <iframe
              title="PDF-Vorschau"
              src={pdfPreviewUrl}
              className="pdf-preview-frame"
            />
          </div>
        </div>
      )}

      {/* ─── Kontextmenü (Rechtsklick) ─── */}
      {contextMenu && (
        <>
          <div
            className="context-menu-overlay"
            onClick={() => setContextMenu(null)}
          />
          {contextMenu.type === "aura" ? (
            <div
              className="context-menu"
              style={{
                top: contextMenu.screenY,
                left: contextMenu.screenX,
              }}
            >
              <button onClick={() => handleAuraDuplicate(contextMenu.auraId)}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="8" y="8" width="11" height="11" rx="2" />
                  <path d="M5 16H4a2 2 0 01-2-2V5a2 2 0 012-2h9a2 2 0 012 2v1" />
                  <path d="M13.5 11v5" />
                  <path d="M11 13.5h5" />
                </svg>
                Duplizieren
              </button>
              <div className="context-menu-divider" />
              <div className="context-menu-submenu">
                <button className="context-menu-submenu-trigger">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 3a9 9 0 100 18 2.2 2.2 0 002.1-2.9 2.2 2.2 0 012.8-2.8A9 9 0 0012 3z" />
                    <circle
                      cx="8"
                      cy="11"
                      r="0.8"
                      fill="currentColor"
                      stroke="none"
                    />
                    <circle
                      cx="11"
                      cy="8"
                      r="0.8"
                      fill="currentColor"
                      stroke="none"
                    />
                    <circle
                      cx="15"
                      cy="9"
                      r="0.8"
                      fill="currentColor"
                      stroke="none"
                    />
                  </svg>
                  <span>Einfärben</span>
                  <span className="context-menu-arrow">›</span>
                </button>
                <div className="context-menu-submenu-panel">
                  {AURA_COLOR_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() =>
                        handleAuraColorChange(contextMenu.auraId, opt.value)
                      }
                    >
                      <span
                        className="color-dot"
                        style={{ background: opt.value }}
                      />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="context-menu-divider" />
              <button
                onClick={() => handleAuraDeleteFromMenu(contextMenu.auraId)}
                className="context-menu-delete"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 6h18" />
                  <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                </svg>
                Löschen
              </button>
            </div>
          ) : (
            <div
              className="context-menu"
              style={{
                top: contextMenu.screenY,
                left: contextMenu.screenX,
              }}
            >
              <button onClick={handleCopy}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
                Kopieren
              </button>
              <button onClick={handleDuplicateSelected}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="8" y="8" width="11" height="11" rx="2" />
                  <path d="M5 16H4a2 2 0 01-2-2V5a2 2 0 012-2h9a2 2 0 012 2v1" />
                  <path d="M13.5 11v5" />
                  <path d="M11 13.5h5" />
                </svg>
                Duplizieren
              </button>
              <div className="context-menu-divider" />
              <button
                onClick={() => handleAlignSelection("top")}
                disabled={selectedIds.length < 2}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 5h16" />
                  <path d="M7 10h10" />
                  <path d="M9 15h6" />
                </svg>
                Oben ausrichten
              </button>
              <button
                onClick={() => handleAlignSelection("bottom")}
                disabled={selectedIds.length < 2}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 9h6" />
                  <path d="M7 14h10" />
                  <path d="M4 19h16" />
                </svg>
                Unten ausrichten
              </button>
              <button
                onClick={handleDistributeSelection}
                disabled={selectedIds.length < 3}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="5" cy="12" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="19" cy="12" r="2" />
                  <path d="M5 5v14" />
                  <path d="M19 5v14" />
                </svg>
                Gleichmäßig verteilen
              </button>
              <button
                onClick={handleDeleteEverySecond}
                disabled={selectedIds.length < 2}
                title="Schachbrett-Ausdünnung: halbiert die Sensordichte in der Auswahl"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="4" y="4" width="16" height="16" rx="1" />
                  <path d="M4 12h16" />
                  <path d="M12 4v16" />
                  <rect
                    x="4"
                    y="4"
                    width="8"
                    height="8"
                    fill="currentColor"
                    stroke="none"
                    opacity="0.35"
                  />
                  <rect
                    x="12"
                    y="12"
                    width="8"
                    height="8"
                    fill="currentColor"
                    stroke="none"
                    opacity="0.35"
                  />
                </svg>
                Jeden zweiten löschen
              </button>
              <div className="context-menu-divider" />
              <div className="context-menu-submenu">
                <button className="context-menu-submenu-trigger">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 3a9 9 0 100 18 2.2 2.2 0 002.1-2.9 2.2 2.2 0 012.8-2.8A9 9 0 0012 3z" />
                    <circle
                      cx="7.5"
                      cy="10.5"
                      r="0.8"
                      fill="currentColor"
                      stroke="none"
                    />
                    <circle
                      cx="10.5"
                      cy="7.5"
                      r="0.8"
                      fill="currentColor"
                      stroke="none"
                    />
                    <circle
                      cx="14.5"
                      cy="7.8"
                      r="0.8"
                      fill="currentColor"
                      stroke="none"
                    />
                  </svg>
                  <span>Einfärben</span>
                  <span className="context-menu-arrow">›</span>
                </button>
                <div className="context-menu-submenu-panel">
                  <button
                    onClick={() => applyColorToSelection(SENSOR_COLORS.green)}
                  >
                    <span className="color-dot color-dot-green" />
                    Grün
                  </button>
                  <button
                    onClick={() => applyColorToSelection(SENSOR_COLORS.orange)}
                  >
                    <span className="color-dot color-dot-orange" />
                    Orange
                  </button>
                  <button
                    onClick={() => applyColorToSelection(SENSOR_COLORS.blue)}
                  >
                    <span className="color-dot color-dot-blue" />
                    Blau
                  </button>
                </div>
              </div>
              <button onClick={handleRotateStart}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 2v6h-6" />
                  <path d="M3 12a9 9 0 0115-6.7L21 8" />
                  <path d="M3 22v-6h6" />
                  <path d="M21 12a9 9 0 01-15 6.7L3 16" />
                </svg>
                Drehen
              </button>
              <div className="context-menu-divider" />
              <button
                onClick={handleDeleteSelected}
                className="context-menu-delete"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 6h18" />
                  <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                </svg>
                Entfernen
              </button>
            </div>
          )}
        </>
      )}

      {/* ─── Paste-Modus Hinweis ─── */}
      {pasteMode && (
        <div className="paste-hint">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
          Klicken Sie auf den Plan, um die kopierten Sensoren einzufügen.
          <button
            onClick={() => {
              setPasteMode(false);
              setClipboard(null);
            }}
          >
            Abbrechen
          </button>
        </div>
      )}

      {/* ─── Dreh-Steuerung (Panel unten, über der Info-Bar) ─── */}
      {rotateMode && (
        <div className="rotate-panel">
          <span className="rotate-panel-label">Auswahl drehen:</span>
          <button
            className="rotate-panel-btn"
            onClick={() => setRotateAngle((a) => Math.max(-180, a - 15))}
          >
            −15°
          </button>
          <button
            className="rotate-panel-btn"
            onClick={() => setRotateAngle((a) => Math.max(-180, a - 1))}
          >
            −1°
          </button>
          <input
            type="number"
            className="rotate-panel-input"
            min={-180}
            max={180}
            value={rotateAngle}
            onChange={(e) => {
              let v = parseInt(e.target.value, 10);
              if (isNaN(v)) v = 0;
              v = Math.max(-180, Math.min(180, v));
              setRotateAngle(v);
            }}
          />
          <span className="rotate-panel-deg">°</span>
          <button
            className="rotate-panel-btn"
            onClick={() => setRotateAngle((a) => Math.min(180, a + 1))}
          >
            +1°
          </button>
          <button
            className="rotate-panel-btn"
            onClick={() => setRotateAngle((a) => Math.min(180, a + 15))}
          >
            +15°
          </button>
          <button className="rotate-panel-apply" onClick={applyRotation}>
            Übernehmen
          </button>
          <button
            className="rotate-panel-cancel"
            onClick={() => {
              setRotateMode(false);
              setRotateAngle(0);
              setRotateCenter(null);
            }}
          >
            Abbrechen
          </button>
        </div>
      )}

      {/* ─── Info-Leiste fix am unteren Bildschirmrand (nur Schritt 5) ─── */}
      {step === 5 && (
        <div className="step5-infobar-fixed">
          <span>
            <strong>Quadratmeter der Sensorfläche:</strong> {areaM2?.toFixed(1)}{" "}
            m²
          </span>
          <span className="step5-infobar-sep">|</span>
          <span>
            <strong>HUM-ID Sensoren:</strong>{" "}
            {sensorPoints.length + addedPoints.length - deletedIds.length}
          </span>
          {AURA_FEATURE_ENABLED && (
            <>
              <span className="step5-infobar-sep">|</span>
              <span>
                <strong>Aktive HUM-ID Auras:</strong> {auraPoints.length}
              </span>
            </>
          )}
          {projectNotice && (
            <span className="step5-project-notice">{projectNotice}</span>
          )}
          <div
            className="step5-background-controls"
            title="Farbe und Transparenz der Sensorflächen-Hinterlegung"
          >
            <label className="step5-background-select-wrap">
              <span className="step5-background-label">Hintergrundfarbe</span>
              <select
                className="step5-background-select"
                value={areaOverlayMode}
                onChange={(e) =>
                  setAreaOverlayMode(normalizeAreaOverlayMode(e.target.value))
                }
                disabled={
                  areaDeleteMode ||
                  addMode ||
                  rasterDraggable ||
                  moveSelectionMode ||
                  auraMode
                }
              >
                {AREA_OVERLAY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="step5-background-opacity">
              <span>Transparenz</span>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={areaOverlayTransparency}
                onChange={(e) =>
                  setAreaOverlayTransparency(
                    clampAreaOverlayTransparency(e.target.value)
                  )
                }
                disabled={
                  areaOverlayMode === "none" ||
                  areaDeleteMode ||
                  addMode ||
                  rasterDraggable ||
                  moveSelectionMode ||
                  auraMode
                }
                aria-label="Transparenz der Hintergrundfarbe"
              />
              <span className="step5-background-opacity-value">
                {areaOverlayTransparency}%
              </span>
            </label>
          </div>
          <button
            className="step5-infobar-project"
            onClick={handleSaveProject}
            disabled={
              areaDeleteMode ||
              addMode ||
              rasterDraggable ||
              moveSelectionMode ||
              auraMode
            }
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none">
              <path
                d="M5 3h12l2 2v16H5z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M8 3v6h8V3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M8 15h8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Projekt speichern
          </button>
          <div className="step5-infobar-more">
            <button
              type="button"
              className="step5-infobar-more-btn"
              aria-haspopup="true"
              aria-label="Weitere Aktionen: Download DXF und PDF gestalten"
              title="Weitere Aktionen"
            >
              <svg
                viewBox="0 0 24 24"
                width="17"
                height="17"
                fill="currentColor"
              >
                <circle cx="5" cy="12" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="19" cy="12" r="2" />
              </svg>
            </button>
            <div className="step5-infobar-more-menu" role="menu">
              <button
                role="menuitem"
                onClick={handleDownloadDxf}
                disabled={
                  areaDeleteMode ||
                  addMode ||
                  rasterDraggable ||
                  moveSelectionMode ||
                  auraMode ||
                  !mPerPx
                }
                title="Sensorpunkte, Auras und Sensorfläche als DXF für CAD exportieren"
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none">
                  <path
                    d="M4 4h10l6 6v10H4z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 4v6h6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                </svg>
                Download DXF
              </button>
              <button
                role="menuitem"
                onClick={() => setStep(6)}
                disabled={
                  areaDeleteMode ||
                  addMode ||
                  rasterDraggable ||
                  moveSelectionMode ||
                  auraMode
                }
                title="Projektangaben, Produktübersicht und Abdeckblöcke für den PDF-Printout gestalten"
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none">
                  <rect
                    x="3"
                    y="3"
                    width="18"
                    height="18"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path d="M3 9h18" stroke="currentColor" strokeWidth="2" />
                  <path d="M9 9v12" stroke="currentColor" strokeWidth="2" />
                </svg>
                PDF gestalten
              </button>
            </div>
          </div>
          <button
            className="step5-infobar-download"
            onClick={() => handleDownload({ withFrame: false })}
            disabled={
              areaDeleteMode ||
              addMode ||
              rasterDraggable ||
              moveSelectionMode ||
              auraMode ||
              isPdfBusy
            }
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none">
              <path
                d="M12 3v12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M8 13l4 4 4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 21h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Download PDF
          </button>
        </div>
      )}
    </>
  );
}
